// Type-only React import so `React.CSSProperties` (Theme) resolves under EVERY tsconfig —
// including the node/CommonJS functions config, which pulls this file in via the pure quiz-
// scoring services imported by netlify functions. Erased at runtime; no bundle impact.
import type * as React from 'react';

// ─── Admin / School Entity Types (Phase 1) ──────────────────────────────────

// 'pending' is a schoolRoles sentinel for a teacher who joined by code but isn't approved
// yet — it grants NO data access (not in the rules' school-access allow-lists); approval
// flips it to 'teacher'. It never appears as a schoolAssociation role.
export type OrgRole = 'board_admin' | 'principal' | 'vp' | 'office_staff' | 'teacher' | 'pending';
export type SisProfile = 'aspen_arc' | 'powerschool_csv' | 'oneroster' | 'custom' | 'manual';
export type EnterpriseSubscriptionTier = 'small' | 'medium' | 'large' | 'board';

export interface SchoolAssociation {
  schoolId: string;
  boardId: string;
  role: OrgRole;
  context: string; // school name for display in context switcher
}

export interface BoardBranding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoUrl: string;
}

export interface BoardDocument {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string; // uid of Board Admin
  stripeCustomerId?: string;
  subscriptionTier?: EnterpriseSubscriptionTier;
  subscriptionStatus?: 'active' | 'past_due' | 'canceled';
  schoolIds: string[];
  country: string; // e.g. "CA"
  province: string; // e.g. "ON"
  defaultSisProfile: SisProfile;
  /** Board-level visual identity. Renders in the admin sidebar when the
   *  board admin is at the board view (no school selected) and on the
   *  school list cards as a fallback when a school has no own logo. */
  branding?: BoardBranding;
}

export interface SchoolPeriod {
  name: string;
  startTime: string; // "HH:MM"
  endTime: string;
}

export interface SchoolDocument {
  id: string;
  name: string;
  boardId: string;
  schoolCode: string; // unique 6–8 alphanumeric, teachers use this to request joining
  /** Set when this school is the managed school behind an enterprise billing domain.
   *  Marks it "enterprise-managed": branding stays owner-controlled (its principals/VPs
   *  don't get manage_branding). See enterprise-admin.setupOrgSchool + permissions.ts. */
  enterpriseDomain?: string;
  branding: BoardBranding;
  /**
   * Surfaces student hub questions nobody has answered yet, so a principal can
   * see "four students have been waiting two days" as a fact rather than a
   * feeling. ON unless explicitly disabled — the point is that nobody has to
   * remember to turn it on. See services/awaitingReply.ts for the rule (notably:
   * the clock restarts from a student's follow-up, not the original question).
   */
  /**
   * The school's own calendar: when each semester or term runs.
   *
   * Separate from reporting periods on purpose. A reporting period is a
   * DEADLINE inside a term; the term is the span it sits in. Hanging the
   * overview's progress bar off reporting periods alone meant the school had no
   * sense of time at all until someone opened a period, and none between them —
   * which is most of the year.
   *
   * With terms set, the bar is always there and reporting periods become
   * markers ON it, which is what makes it answer "where are we, and what's
   * next" rather than just "how long until Friday".
   */
  academicTerms?: {
    id: string;
    /** "Semester 1", "Fall Term", "Quadmester 2" — the school's own words. */
    name: string;
    /** YYYY-MM-DD, read as a LOCAL date (see reportingClock.parseLocalDate). */
    startDate: string;
    endDate: string;
  }[];
  /**
   * May an admin opening a class read-only also see its CLASS HUB?
   *
   * The hub carries student questions and conversation, which is a different
   * kind of thing from marks and attendance. Some schools want oversight to
   * include it; others consider a student's messages to their teacher none of
   * the office's business. Defaults to ON — the point of oversight is to see
   * the class — but a school can turn it off and the server stops sending it,
   * so it is withheld rather than hidden.
   */
  adminClassViewIncludesHub?: boolean;
  awaitingReplyMonitor?: {
    enabled?: boolean;
    /** Hours before a question counts as overdue. Clamped 1-336; default 24. */
    thresholdHours?: number;
  };
  periods: SchoolPeriod[];
  sisProfile: SisProfile;
  /** Where the school physically is. Collected at creation (drives the SIS-profile
   *  default) and now PERSISTED — previously it was asked and thrown away, leaving
   *  nothing to hang jurisdiction-specific behaviour on. */
  country?: string;
  province?: string;
  /**
   * Does this school grant Ontario credits / report to OnSIS?
   *
   * Deliberately NOT derived from country/province: an Ontario-inspected private
   * school can sit anywhere in the world (e.g. in Italy, granting OSSD credits),
   * and a school physically in Ontario may grant no credits at all. Defaulted from
   * geography at creation, always overridable.
   *
   * This is the gate for the whole Ontario compliance surface — OEN as required,
   * BSID, student address, credit values, compulsory-credit flags, community
   * involvement hours, OSSLT status, per-period mark snapshots, and the
   * OnSIS/OST/provincial-report-card outputs. Schools with it off never see any of
   * that weight.
   */
  grantsOntarioCredits?: boolean;
  /** Board/School Identification Number — the ministry's school identifier.
   *  Required to submit to OnSIS; only meaningful when grantsOntarioCredits. */
  bsid?: string;
  /** Community-involvement hours this school requires for graduation. Ontario's default
   *  is 40, but a board may set more, fewer, or 0 (not required). Undefined ⇒ 40. */
  communityHoursRequired?: number;
  /** Allow teachers in this school to create anonymized "nickname" (privacy-mode)
   *  classes. Default OFF for schools: nickname classes strip student PII (real
   *  name, OEN, email), which conflicts with SIS/OnSIS/report-card obligations.
   *  A board admin/principal can turn it on. Independent (personal) teachers are
   *  never gated by this. */
  allowNicknameClasses?: boolean;
  sisCustomConfig?: Record<string, unknown>; // for 'custom' profile
  subscriptionStatus?: 'active' | 'past_due' | 'canceled'; // inherited from parent board
  createdAt: string;
  createdBy: string;
}

export interface SchoolStaffRecord {
  uid: string;
  /** Denormalized from the user profile at invitation/accept time
   *  (mirrors the same field on SchoolTeacherRecord). Lets the
   *  staff management UI render a human-readable label without
   *  having to read /users/{uid} — which firestore.rules denies
   *  for anyone other than the owner. */
  displayName?: string;
  email?: string;
  role: 'principal' | 'vp' | 'office_staff';
  invitedBy: string;
  joinedAt: string;
  status: 'active' | 'pending' | 'removed';
  /** Set when status flips to 'removed' (off-boarded from the school). */
  removedAt?: string;
  /** Delegated permissions — only applies to office_staff role.
   *  Principals and VPs can toggle these per staff member.
   *  Default for new office staff: ['manage_attendance_excused'] */
  delegatedPermissions?: string[];
}

export interface SchoolTeacherRecord {
  uid: string;
  displayName?: string;  // denormalized from user profile at invitation/approval time
  email?: string;        // denormalized from user profile at invitation/approval time
  joinedAt: string;
  joinMethod: 'invited' | 'school_code' | 'enterprise_seat';
  invitedBy: string | null;
  /**
   * Lifecycle of the school-teacher relationship.
   *  - active: normal — appears in timetabling, emails, etc.
   *  - inactive: soft-disabled by an admin. Record stays for history
   *    + dept-membership but the teacher is excluded from the
   *    timetable generator and bulk-email pools. Reversible with a
   *    single click — distinct from `removed` which is the formal
   *    off-boarding path.
   *  - pending_approval: just joined / just imported; awaiting admin
   *    approve.
   *  - removed: off-boarded. dataRetentionExpiry counts down to a
   *    hard delete.
   */
  status: 'active' | 'inactive' | 'pending_approval' | 'removed';
  removedAt: string | null;
  brandingToggle: boolean; // default true
  dataRetentionExpiry: string | null; // removedAt + 7 days
  /** Teacher's home department (Math, Science, English, etc). Free-
   *  text but typically matches one of the school's known dept names
   *  so the timetable Reports + Catalog UIs can group/filter by it.
   *  Generator uses this as a soft preference (prefer assigning
   *  same-dept courses) but never as a hard constraint — admins can
   *  always pin a teacher to a course in TeacherAssignments. */
  homeDepartment?: string;
  /** True for placeholder records the admin created for capacity
   *  planning before an actual hire was confirmed (e.g. "we'll have
   *  a math teacher named TBD"). Behaves like a regular active
   *  teacher for capacity math and timetable generation, but
   *  surfaces at the top of the Teachers list with a GUEST badge so
   *  admins can find and convert / replace them later. Toggle off
   *  once the real teacher is confirmed. */
  isGuest?: boolean;
}

export interface InvitationDocument {
  id: string;
  schoolId: string;
  boardId: string;
  invitedEmail: string;
  targetRole: OrgRole;
  invitedBy: string;
  token: string;
  createdAt: string;
  expiresAt: string; // createdAt + 7 days
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  /** Snapshot of the school's display name at invite-creation time.
   *  Embedded so AcceptInvitePage can render the school chip for a
   *  recipient who isn't signed in yet (the /schools/{id} rule
   *  requires a school association the invitee doesn't have until
   *  they accept). Optional for back-compat with invites created
   *  before this field landed. */
  schoolName?: string;
}

export interface ClassTransferDocument {
  id: string;
  classId: string;
  fromTeacherUid: string;
  toTeacherUid: string;
  initiatedBy: string;
  schoolId: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  createdAt: string;
  resolvedAt: string | null;
}

// ─── Phase 3: School Student Directory ───────────────────────────────────────

/** A student record in the school-level directory (separate from class-embedded students) */
export interface SchoolStudent {
  id: string;
  schoolId: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  studentNumber: string;
  oen?: string;              // Ontario Education Number — links to Aspen .arc imports
  email?: string;
  grade?: string;            // e.g. "9", "10", "11", "12"
  gender?: string;           // free-text — used for sortable directory columns + reporting
  /** ISO date string (YYYY-MM-DD) — drives the Age column in the directory.
   *  Stored as a date string (not Timestamp) so it survives Firestore reads
   *  intact and lines up with how dates are formatted in the rest of the app. */
  dateOfBirth?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  notes?: string;
  // Demographics. Address is required by any SIS record of a student; its absence
  // is an instant disqualifier in an SIS evaluation. Medical alert is surfaced to
  // staff and substitutes.
  streetAddress?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  /** Allergies / medical conditions staff and substitutes must be aware of. */
  medicalAlert?: string;
  createdAt: string;
  updatedAt: string;
  /** Total credits earned across all completed courses (year-over-year). */
  creditsEarned?: number;
  /** Course codes the student has earned a passing grade in. Used for
   *  prerequisite checks at course-selection time. */
  completedCourseCodes?: string[];
  /** Community-involvement hours completed (default OSSD requirement is 40, but the
   *  school can configure a different threshold — see SchoolData.communityHoursRequired).
   *  This is the sum of APPROVED log entries, unless communityHoursOverride is set. */
  communityHours?: number;
  /** Manual override of the community-hours total — when set, it's used instead of the
   *  approved-log sum (for records entered outside the log / prior-school carryover). */
  communityHoursOverride?: number;
  /** When true, the community-involvement requirement is WAIVED for this student (e.g.
   *  a dual-enrolment student, or a board that doesn't require it) — counts as met. */
  communityHoursWaived?: boolean;
  /** Ontario literacy (OSSLT) requirement status. 'osslt' = passed the test,
   *  'olc' = completed the OLC course in lieu, 'not_yet' = outstanding. */
  ossltStatus?: 'osslt' | 'olc' | 'not_yet';
  // ── OnSIS enrolment-register fields ──────────────────────────────────────────
  // The Ministry submission needs these beyond identity + achievement. Populated from
  // the PowerSchool import where available; code values are the OnSIS tables in
  // src/services/onsisCodes.ts. All optional — only meaningful for OnSIS-reporting schools.
  /** Date the student entered THIS school (OnSIS ENROLMENT_START_DATE). ISO date. */
  enrolmentStartDate?: string;
  /** Date the student left THIS school (OnSIS ENROLMENT_END_DATE). ISO date. */
  enrolmentEndDate?: string;
  /** Admission mobility code — where they came from (ONSIS_ENTRY_CODES). */
  entryCode?: string;
  /** Departure mobility code — transfer/retirement reason (ONSIS_EXIT_CODES). */
  exitCode?: string;
  /** Full-time equivalent, 0.04–1.00 (OnSIS FTE). */
  fte?: number;
  /** FT / HT / PT / LT (ONSIS_ATTENDANCE_TYPE). */
  attendanceType?: string;
  /** Citizenship/immigration residence status (ONSIS_RESIDENCE_STATUS). */
  residenceStatus?: string;
  /** Board resident status (ONSIS_BOARD_RESIDENCE_STATUS). */
  boardResidenceStatus?: string;
  /** Language first spoken / mother tongue (ONSIS_LANGUAGE_TYPE). */
  languageFirstSpoken?: string;
  /** OnSIS MAIN_SCHOOL_FLAG — true for the student's primary school (default true). */
  mainSchoolFlag?: boolean;
  /** Individual Education Plan on file (OnSIS INDIVIDUAL_EDUCATION_PLAN_FLAG). */
  iepFlag?: boolean;
  /** Home/household phone (PowerSchool Home_Phone). */
  homePhone?: string;
  // ── Additional OnSIS fields (from the OnSIS entry screens) ────────────────────
  /** Ontario Scholar designation (80%+ across six Grade-12 U/M courses) + award date. */
  ontarioScholar?: boolean;
  ontarioScholarDate?: string;      // ISO date
  /** Specialist High Skills Major (SHSM) program participant. */
  shsmProgram?: boolean;
  /** Mature student (OnSIS mature-student status). */
  matureStudent?: boolean;
  /** Reach Ahead — taking a credit ahead of their current grade. */
  reachAhead?: boolean;
  /** Special-education/exceptionality on file (distinct from the IEP flag). */
  specialEducation?: boolean;
  /** Exceptionality code (ONSIS_EXCEPTIONALITY). */
  exceptionality?: string;
  /** Grade-designation basis (ONSIS_GRADE_DESIGNATION): years / credits / principal. */
  gradeDesignation?: string;
  /** Free-text gender description when the coded gender needs a specifier. */
  genderSpecify?: string;
  /** Country of CURRENT residence — key for overseas students (e.g. a school in Italy). */
  countryOfResidence?: string;
  /** Year of entry to Canadian schooling (YYYY or YYYY-MM). */
  yearOfEntry?: string;
  /** Online Learning Graduation Requirement status (ONSIS_OLG_REQUIREMENT). */
  onlineLearningGradReq?: string;
  /** PLAR (Prior Learning Assessment & Recognition) attendance type (ONSIS_ATTENDANCE_TYPE). */
  plarAttendanceType?: string;
  /** Diploma / certificate awarded (ONSIS_DIPLOMA_TYPE) + date granted. */
  diplomaType?: string;
  diplomaDate?: string;             // ISO date
  /** Province/state of CURRENT residence (pairs with countryOfResidence). */
  currentResidenceProvince?: string;
  /** Country + province/state of BIRTH (OnSIS COUNTRY_TYPE_BIRTH / PROVINCE_STATE_TYPE_BIRTH). */
  birthCountry?: string;
  birthProvince?: string;
  /** First Nations / Métis / Inuit voluntary self-identification (ONSIS_INDIGENOUS_SELF_ID). */
  indigenousSelfId?: string;
  /** Second Language (FSL) program type (ONSIS_FSL_PROGRAM) + minutes of
   *  instruction per day. Reported with Student School Enrolment. */
  secondLanguageProgram?: string;
  secondLanguageMinutes?: number;

  /** Set when graduated. Read-only after graduation; deletion happens via
   *  the 365-day soft-delete + manual permanent delete flow. */
  graduatedAt?: string;
  /** When set, student is archived and not shown in active directory listings.
   *  Written by the withdrawal flow (mid-year leave) and by graduation rollover. */
  archivedAt?: string;
  /** Why the student was archived/withdrawn — e.g. 'moved', 'transferred',
   *  'graduated', free text. OnSIS wants the exit reason, not just the date. */
  archiveReason?: string;
  archivedBy?: string;
  /**
   * Soft-delete marker. When set, the directory student is hidden everywhere and
   * slated for permanent deletion after `dataRetentionExpiry`, but ALL records
   * (courseHistory, earnedCredits, mark snapshots) are preserved until then —
   * OnSIS must never lose an active record. Replaces the old hard delete.
   */
  deletedAt?: string;
  deletedBy?: string;
  /** ISO date after which a soft-deleted student may be permanently purged
   *  (deletedAt + 365 days). Nothing purges before this. */
  dataRetentionExpiry?: string;
  // OneRoster source tracking
  oneRosterSourcedId?: string;
  oneRosterLastSyncAt?: string;
  sourceSystem?: 'manual' | 'sis_import' | 'oneroster';
}

/**
 * Generic teacher-grouping doc. Covers both formal departments (Math,
 * English, ...) and ad-hoc cohorts (SHSM Health, Drama Club Staff,
 * field-trip group). Departments are the canonical scheduling unit;
 * custom groups are useful for bulk emailing and reporting cohorts that
 * don't fit a departmental shape.
 */
export interface SchoolGroup {
  id: string;
  schoolId: string;
  name: string;
  description?: string;
  /** 'department' surfaces alongside the auto-derived ones from teacher
   *  assignments; 'custom' is anything else (SHSM, lunch monitors, etc.). */
  type: 'department' | 'custom';
  /**
   * Sub-category for custom groups. Used for sorting / sectioning in the
   * Departments & Groups panel — Department, Club, Team, Special each
   * render in their own section. Missing on legacy records, defaulted
   * to 'special' (the catch-all bucket) at read time.
   */
  category?: 'department' | 'club' | 'team' | 'special';
  /** Auth uids (or stub uids for imported teachers) of group members. */
  memberUids: string[];
  createdAt: string;
  createdBy: string;
}

// ─── School Email System ────────────────────────────────────────────────────

export interface SchoolEmail {
  id: string;
  schoolId: string;
  authorUid: string;
  authorName: string;
  recipientType: 'parent' | 'student' | 'teacher' | 'custom';
  recipientEmail: string;
  recipientName: string;
  subject: string;
  body: string;
  status: 'draft' | 'sent';
  createdAt: string;
  sentAt?: string;
}

export interface SchoolEmailTemplate {
  id: string;
  schoolId: string;
  name: string;
  subject: string;
  body: string;
  category: 'absence' | 'parent_communication' | 'teacher_notice' | 'general';
  createdBy: string;
  createdAt: string;
}

// ─── Enrollment Bridge ──────────────────────────────────────────────────────

/** Links a school directory student to a class. Foundation for timetabling. */
export interface Enrollment {
  id: string;
  schoolId: string;
  schoolStudentId: string;  // ref to schools/{schoolId}/students/{studentId}
  classId: string;          // ref to classes/{classId}
  status: 'active' | 'dropped' | 'transferred';
  source: 'timetable' | 'manual' | 'join_code' | 'sis_import';
  enrolledAt: string;
  enrolledBy?: string;      // uid of teacher/admin who enrolled, or 'system' for SIS
  droppedAt?: string;       // exit-episode date (set when status → dropped/transferred)
  droppedBy?: string;       // uid of teacher/admin who recorded the exit
  droppedReason?: string;   // optional exit reason (moved, transferred, etc.)
}

// ─── Reporting Periods (Phase 3) ─────────────────────────────────────────────

export interface ReportingPeriod {
  id: string;
  schoolId: string;
  name: string;
  startDate: string;
  deadlineDate: string;
  status: 'active' | 'closed';
  createdBy: string;
  createdAt: string;
  templateFields?: ReportTemplateField[];
  /** When set, report cards for this period are visible to students */
  releasedToStudentsAt?: string;
  releasedToStudentsBy?: string;
  /** When set, report cards for this period are visible to parents (parent portal) */
  releasedToParentsAt?: string;
  releasedToParentsBy?: string;
  /**
   * When set, the frozen report-card snapshot for this period has been captured
   * (one doc per class under reportCardSnapshots/{classId}). Set the first time
   * either audience is released; the snapshot is then reused (never rebuilt) so
   * released values stay frozen. Both portals require this AND the matching
   * released* flag before reading a period's snapshot.
   */
  reportCardsSnapshotAt?: string;
  /**
   * Which term this period reports on ('S1' | 'S2' | 'FY'). Used by the academic
   * ledger to decide a course's final mark: a course's official grade is the mark
   * frozen at that term's FINAL period, not an earlier progress period.
   */
  term?: 'S1' | 'S2' | 'FY';
  /**
   * True for the terminal reporting period of its term (June for a full-year or
   * semester-2 course, January for semester 1). A ledger entry projected from a
   * final period is 'final'; from any earlier period it's 'provisional'. Admin-set
   * on the period, so correcting which period is terminal is picked up on the next
   * ledger rebuild without re-freezing any marks.
   */
  isFinalForTerm?: boolean;
}

/**
 * ── Mark snapshots (the per-period spine) ─────────────────────────────────────
 * Marks in the gradebook are a LIVE stream with no time dimension — exports always
 * read the current value, so "what was this student's mark in the October cycle?"
 * was unanswerable. A snapshot freezes one class's marks at the moment the teacher
 * submits them for a reporting period.
 *
 * The snapshot is authoritative FOR ITS PERIOD (that's the point); the gradebook
 * stays authoritative for the current mark. Anything period-scoped — OnSIS cycles,
 * the OST course history, provincial report cards — reads the snapshot.
 *
 * Stored at schools/{schoolId}/reportSubmissions/{periodId}/teachers/{uid}/marks/{classId}
 * — the existing submission chokepoint, whose rules already limit reads to the
 * submitting teacher + school admins (students can never read it).
 */
export interface MarkSnapshotRow {
  /** The CLASS roster student id (ephemeral, per-class). */
  studentId: string;
  /** The durable school-directory id (SchoolStudent.id). This — NOT studentId — is
   *  the academic ledger's key: the class roster entry gets a fresh id per class,
   *  but the directory identity is stable across classes and years. Absent for a
   *  student not linked to the school directory (who isn't in the SIS). */
  schoolStudentId?: string;
  /** OEN at capture time — the SIS join key (absent if the student has none). */
  oen?: string;
  /**
   * The transcript course code THIS student earned, when it differs from the
   * class's.
   *
   * A combined section (ENG3U taught with EWC4U) is one class whose code is
   * "ENG3U/EWC4U" — not a course anyone can be granted a credit in. Each
   * student earns exactly one half. Without this the snapshot carries a single
   * code for everyone and the academic ledger, which keys on it, grants half
   * the class a credit in a course they never took.
   *
   * Absent for an ordinary class, where the snapshot's own courseCode is right
   * for every row.
   */
  courseCode?: string;
  /** Name as it stood at capture (students get renamed; the record shouldn't drift). */
  studentName: string;
  /** The effective mark at capture, via the same chain the SIS export uses
   *  (hub average → pending hub average → calculated + offset). Null = no mark. */
  finalMark: number | null;
  /** Where finalMark came from, so a later reader can audit it.
   *  'adjusted' = the calculated average plus a teacher offset. Distinguished from
   *  'calculated' because on a transcript years later, an adjusted 72 and a raw 72
   *  are not the same fact — and this record is the only place the difference
   *  survives (the offset itself lives on a class that may be long deleted). */
  source: 'hub' | 'pending' | 'adjusted' | 'calculated' | 'missing';
  /** Ontario learning skills (E/G/S/N) as submitted. */
  learningSkills?: Record<string, string>;
  reportComment?: string;
  /** Credit outcome at capture — computed from the mark (see creditStatus.ts).
   *  'granted' | 'not_granted' | 'incomplete'. This + creditValue on the snapshot
   *  is what OnSIS cycles and the OST course history actually record. */
  completionStatus?: 'granted' | 'not_granted' | 'incomplete';
}

export interface ClassMarkSnapshot {
  schoolId: string;
  periodId: string;
  teacherUid: string;
  classId: string;
  className: string;
  /** The class code = the SIS course+section stand-in (see sisExport.sisCourseKey). */
  classCode: string;
  /** The bare transcript course code, section stripped ("ADA3M" from "ADA3M102").
   *  Frozen here because the academic ledger keys on it and the class it came from
   *  can be deleted after the snapshot is taken — the whole reason the ledger reads
   *  snapshots, not classes. Derived via courseCode.deriveCourseCode. */
  courseCode?: string;
  /** True when courseCode couldn't be split confidently (non-standard code) and is
   *  the raw class code — a human should confirm it before it's trusted on a transcript. */
  courseCodeInferred?: boolean;
  /** The school year this course ran in ("2025-2026"), frozen for the same reason:
   *  the ledger groups a student's history by year and the class may be gone. */
  academicYear?: string;
  semester?: string;
  /** Credit value of the course at capture (frozen — a class's credit value could
   *  be corrected later, but this period's record shouldn't silently change). */
  creditValue: number;
  capturedAt: string;
  rows: MarkSnapshotRow[];
}

/**
 * ── Student Academic Ledger (the Tier-1 SIS spine) ────────────────────────────
 * A per-student, per-year record of every course taken and its outcome — the
 * structure a transcript (OST), OSSD credit-counselling and OnSIS achievement all
 * read from. "What has this student earned since Grade 9?" is one collection read
 * here, versus a scatter-read across every period of every year of the snapshots
 * (which also breaks when a class is deleted).
 *
 * Stored at schools/{schoolId}/students/{studentId}/courseHistory/{entryId}.
 *
 * IMPORTANT — this is a DERIVED PROJECTION, not a second source of truth. Entries
 * with origin 'snapshot' are regenerated by rebuild-student-ledger from the frozen
 * mark snapshots and can always be rebuilt, so they can never permanently drift.
 * Entries with origin 'import' or 'manual' (prior-year backfill, Aspen imports)
 * ARE source data — a rebuild must never touch them. Server-written only.
 */
export interface CourseHistoryEntry {
  /** For snapshot-origin entries this is deterministic ("snap_{classId}") so a
   *  rebuild upserts rather than duplicating. Import/manual entries get their own ids. */
  id: string;
  studentId: string;
  schoolId: string;
  schoolYear: string;              // "2025-2026"
  term?: 'S1' | 'S2' | 'FY';
  courseCode: string;              // transcript code, section stripped ("SBI3U")
  courseCodeInferred?: boolean;    // the split wasn't confident — confirm before trusting
  courseTitle: string;
  gradeLevel?: string;             // "11"
  courseType?: string;             // Ontario course-type letter: U/M/C/O/E …
  creditValue: number;
  finalMark: number | null;
  completionStatus: 'granted' | 'not_granted' | 'incomplete';
  /** 'provisional' until the course's terminal reporting period closes (see
   *  ReportingPeriod.isFinalForTerm); 'final' once it has. */
  status: 'provisional' | 'final';
  /** Where this entry came from. Only 'snapshot' entries are rebuilt; 'import' and
   *  'manual' are authoritative source data a rebuild leaves untouched. */
  origin: 'snapshot' | 'import' | 'manual';
  sourcePeriodId?: string;         // provenance (snapshot origin)
  sourceClassId?: string;
  capturedAt: string;
  updatedAt: string;
}

/**
 * ── Community-involvement hours ───────────────────────────────────────────────
 * One logged community-service activity toward the OSSD 40-hour requirement. Stored
 * at schools/{schoolId}/students/{studentId}/communityHours/{entryId}, written by an
 * admin/office/guidance member (manage_students). The APPROVED entries' hours are
 * summed into SchoolStudent.communityHours (the total the graduation engine + OST
 * read), so itemising here supersedes the old single manual number.
 */
export interface CommunityHoursEntry {
  id: string;
  studentId: string;               // SchoolStudent id
  schoolId: string;
  activity: string;                // what they did
  organization?: string;           // where / for whom
  hours: number;
  date?: string;                   // when performed (ISO date), optional
  status: 'pending' | 'approved' | 'rejected';
  /** Verification checkmark — the signed hours form / confirmation is on file. */
  signatureOnFile?: boolean;
  notes?: string;                  // supervisor / contact / verification note
  loggedBy: string;                // uid that entered it
  loggedByName?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
}

/**
 * ── Guidance case note ────────────────────────────────────────────────────────
 * A confidential guidance-counsellor note about a student. Stored at
 * schools/{schoolId}/students/{studentId}/guidanceNotes/{noteId}, readable and
 * writable ONLY by guidance staff (the manage_guidance permission) — never regular
 * teachers, students, or parents. Shared among the school's guidance staff for case
 * coverage; each carries its author + timestamp. Immutable once written (OSR-grade
 * discipline); the author may delete their own note to correct an error.
 */
export interface GuidanceNote {
  id: string;
  studentId: string;
  schoolId: string;
  content: string;
  category?: 'general' | 'academic' | 'attendance' | 'wellbeing' | 'meeting' | 'parent-contact' | 'pathway';
  authorUid: string;
  authorName?: string;
  createdAt: string;
}

/**
 * ── Credit discrepancy ────────────────────────────────────────────────────────
 * The ledger auto-creates an EarnedCredit for a passed+final course, but NEVER
 * auto-revokes one — a projection silently stripping a graduation credit is the
 * worst failure mode here. So when a rebuild finds a course that HAS a credit on
 * record but whose current final mark no longer earns it (a resubmission dropped
 * it below the pass mark, say), it records this instead of touching the credit. An
 * admin resolves it deliberately: revoke the credit, or keep it (mark reviewed).
 *
 * Stored at schools/{schoolId}/creditDiscrepancies/{disc_creditId}. Created by the
 * rebuild (server); an admin flips it to resolved. Self-heals: if a later rebuild
 * finds the course passing again, the open discrepancy closes on its own.
 */
export interface CreditDiscrepancy {
  id: string;                  // deterministic: "disc_{creditId}"
  schoolId: string;
  schoolStudentId: string;
  creditId: string;            // the EarnedCredit the marks no longer support
  courseCode: string;
  academicYear: string;
  /** What the ledger says the outcome is now (why the credit is in question). */
  ledgerStatus: 'not_granted' | 'incomplete';
  ledgerMark: number | null;
  status: 'open' | 'resolved';
  detectedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  /** 'revoked' — admin removed the credit. 'kept' — admin reviewed and kept it.
   *  'auto_healed' — a later rebuild found the course passing again. */
  resolution?: 'revoked' | 'kept' | 'auto_healed';
}

export interface ReportSubmission {
  id: string;
  periodId: string;
  schoolId: string;
  teacherUid: string;
  submittedAt: string;
  isLate: boolean;
  classIds: string[];
  status: 'submitted' | 'resubmitted' | 'approved' | 'needs_revision';
  /** Admin feedback/notes on this submission */
  adminFeedback?: string;
  adminFeedbackBy?: string;
  adminFeedbackAt?: string;
}

// ─── SIS / Aspen .arc types (Phase 2) ────────────────────────────────────────

/**
 * One student enrollment record parsed from (or destined for) an Aspen .arc file.
 * Columns are 0-indexed per the confirmed 21-column spec.
 */
export interface ArcEnrollment {
  // Identifiers
  oen: string;                        // col 2 — Ontario Education Number (primary key)
  courseCode: string;                 // col 0 — course code + section concatenated (e.g. ADA3M102)
  semester: string;                   // col 1 — "1" or "2"

  // Flags (stored exactly as received; Y/N strings)
  creditDenied: string;               // col 3
  iepFlag: string;                    // col 4
  eslCode: string;                    // col 5
  accommodationCode: string;          // col 6
  newToCanada: string;                // col 7

  // Marks
  finalMark: number | null;           // col 8 — integer 0-100, null if empty/not set
  midtermMark: string;                // col 9 — stored as string (may be empty)
  eslLevel: string;                   // col 10 — empty if N/A

  // Ontario Learning Skills (E/G/S/N)
  lsResponsibility: string;           // col 11
  lsOrganization: string;             // col 12
  lsIndependentWork: string;          // col 13
  lsCollaboration: string;            // col 14
  lsInitiative: string;               // col 15
  lsSelfRegulation: string;           // col 16

  // Report card comment
  comment: string;                    // col 17 (~200-450 chars)

  // PASSTHROUGH fields — store exactly as received, write back unchanged
  arcCol18_pathway: string;           // col 18 — pathway/credit designation
  arcCol19_relatedSections: string;   // col 19 — related sections

  // CRITICAL: Aspen OID — never generate or modify; store and return verbatim
  aspenScheduleId: string;            // col 20 — Aspen's internal enrollment record key
}

/** Firestore record written after each SIS import */
export interface SisImportLog {
  id: string;
  importedBy: string;
  importedAt: string;
  sisProfile: SisProfile;
  fileName: string;
  totalRows: number;
  imported: number;
  updated: number;
  oidUpdates: number;                 // rows where OID changed (drop/re-enroll detected)
  errors: number;
  errorLog: string[];
}

// ─── Enrollment Firestore document (written by sis-import, read by grade export) ─

export interface EnrollmentDocument {
  id: string;
  studentId: string;             // FK to /students or school student
  oen: string;                   // OEN — primary Aspen key
  classId: string;               // FK to /classes
  courseCode: string;
  sectionCode: string;
  semester: string;
  schoolId: string;
  boardId: string;
  status: 'active' | 'withdrawn';
  aspenScheduleId: string;       // col 20 — CRITICAL: store verbatim
  arcCol18_pathway: string;      // passthrough
  arcCol19_relatedSections: string; // passthrough
  iepFlag: string;
  eslCode: string;
  accommodationCode: string;
  newToCanada: string;
  sourceSystem: 'aspen' | 'manual' | 'oneroster' | 'custom';
  importedAt: string;
}

// ─── Report Template Fields (Phase 4) ────────────────────────────────────────

export interface ReportTemplateField {
  fieldName: string;
  fieldType: 'grade' | 'learning_skill' | 'comment' | 'custom';
  required: boolean;
  charLimit: number | null;
}

// ─── Notifications ───────────────────────────────────────────────────────────

export interface NotificationDocument {
  id: string;
  recipientUid: string;
  schoolId?: string;
  title: string;
  message: string;
  type: string; // e.g. 'deadline_3-day', 'join_request', 'transfer_initiated', 'excused_update'
  /** Who sent it. Stamped by createNotification and pinned to the caller in
   *  firestore.rules, so a notification can't impersonate someone else. */
  senderUid?: string;
  read: boolean;
  createdAt: string;
  actionUrl?: string; // optional deep link
}

// ─── Internal Messaging (Admin ↔ Teacher only) ─────────────────────────────

export interface InternalMessage {
  id: string;
  schoolId: string;
  conversationId: string;      // Groups messages into threads
  senderUid: string;
  senderName: string;
  senderRole: 'principal' | 'vp' | 'office_staff' | 'teacher';
  recipientUid: string;
  recipientName: string;
  subject: string;
  body: string;
  read: boolean;
  createdAt: string;
}

export interface Conversation {
  id: string;
  schoolId: string;
  participantUids: string[];   // Always 2 for direct messages
  participantNames: Record<string, string>; // uid → displayName
  subject: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: Record<string, number>;  // uid → unread count
  createdAt: string;
}

// ─── User types ──────────────────────────────────────────────────────────────

export type PlanType = 'free' | 'basic' | 'pro' | 'ultra' | 'enterprise';

export interface UserProfile {
  uid: string;
  email: string;
  /**
   * Top-level platform role.
   *
   * `board_admin` is the PLATFORM OWNER, despite the name — it grants
   * platform-wide reads and is never handed to a customer. It is displayed as
   * "Owner" everywhere (see services/roleLabels.ts); the stored value is
   * unchanged only to avoid a live migration of a security-critical field that
   * firestore.rules pins immutable.
   *
   * `district_admin` is the role for a REAL board/district administrator: a
   * customer who runs a board of schools. Scoped to the single board named by
   * `districtBoardId` — it is deliberately NOT a weaker board_admin, because
   * anything that reads `board_admin` grants platform-wide access.
   */
  role: 'teacher' | 'student' | 'board_admin' | 'district_admin';
  /**
   * The board a `district_admin` administers. Meaningless for every other role.
   *
   * Scoping lives on the USER doc, not on a board membership subcollection, so
   * firestore.rules can check it in one hop without a second document read —
   * the same reason `schoolRoles` exists alongside `schoolAssociations`.
   */
  districtBoardId?: string;
  displayName: string;
  /** Recovery marker for the post-signup server trial grant. Present only on a
   * newly-created teacher until start-trial or enterprise provisioning consumes it. */
  autoTrialPending?: boolean;
  /** Last app-open, stamped at most once/day (see recordUserActivity). Powers the
   *  owner active-users metric — the only usage signal that includes students. */
  lastActiveAt?: string;
  // School associations — added when teacher/admin joins a school
  schoolAssociations?: SchoolAssociation[];
  /** Parallel map view of schoolAssociations indexed by schoolId,
   *  carrying just the role per school. Exists for firestore.rules
   *  membership checks: rules can't iterate arrays of objects, and
   *  `hasAny` on objects requires exact structural equality —
   *  schoolAssociations entries have 4 fields (schoolId, boardId,
   *  role, context) which never matched the rules' 2-field probe,
   *  effectively denying every non-board-admin access to school
   *  data. This map lets rules check membership in O(1) via a
   *  `schoolId in schoolRoles` lookup. Kept in sync by addSchool-
   *  Association + the accept-invitation Netlify function, with a
   *  one-shot client-side migration in AuthContext for older docs. */
  schoolRoles?: Record<string, OrgRole>;
  title?: 'Mr.' | 'Ms.' | 'Mrs.' | 'Dr.' | 'Prof.' | 'Mx.' | ''; // Teacher prefix/title
  schoolName?: string;
  department?: string;
  /** Free-text job title shown on the admin profile (e.g.
   *  "Vice Principal of Curriculum", "Special Education Coordinator").
   *  Distinct from role / schoolAssociation role — those are the
   *  permission-bearing labels; this is the human-readable one. */
  jobTitle?: string;
  /** Free-text role label for board-level admins who don't have a
   *  school association (e.g. "Superintendent", "Director of
   *  Education"). Only surfaced for users whose schoolAssociations
   *  array is empty. */
  boardRole?: string;

  /** The plan the teacher picked at registration, and when.
   *
   *  A NOTE ABOUT INTENT — never an entitlement. Access is decided solely by
   *  `subscription`, which is server-written and pinned immutable in
   *  firestore.rules. These three fields are client-writable by design, and
   *  nothing may ever grant a capability from them: their only job is to
   *  pre-select the right plan at the paywall when the 7-day trial ends, so
   *  converting is one click rather than a fresh decision. */
  /**
   * Referral fields — ALL server-written, and deliberately absent from the
   * users allowlist in firestore.rules. A client that could set these could
   * name its own referrer after paying, take over somebody's code, or mint
   * itself AI credits. Rules tests pin each one.
   */
  referralCode?: string;
  referredByUid?: string;
  referredAt?: string;
  referralCount?: number;

  intendedPlan?: 'free' | 'basic' | 'pro' | 'ultra';
  intendedBillingCycle?: 'monthly' | 'annual';
  planChosenAt?: string;

  subscription: {
    plan: PlanType;
    status: 'active' | 'canceled' | 'past_due' | 'trialing';
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    currentPeriodEnd?: string;
    /** ISO timestamp a paid subscription lapsed to free (Stripe cancel/expiry).
     *  Starts a 3-day grace; after it, all active classes but one lock until
     *  renewal. Cleared when a paid subscription becomes active again. */
    lapsedAt?: string;
    billingDay?: number; // Day of month subscription renews (1-31)
    discountCode?: string; // Applied discount code
    // Free trial fields. When trialEndsAt is set and in the future, the user
    // has Pro-tier access. After it passes, getEffectivePlan() returns 'free'
    // unless they've converted via Stripe.
    trialStartedAt?: string; // ISO timestamp
    trialEndsAt?: string;    // ISO timestamp — 7 days after start
    hasUsedTrial?: boolean;  // Flag to prevent re-trigger; never cleared once set
    // Set by enterprise-provision (domain capture) when the user belongs to an
    // enterprise org. enterpriseName is the friendly org name for display.
    enterpriseDomain?: string;
    enterpriseName?: string;
    /** The org's managed school, stamped by syncSeatToSchool. Lets the client
     *  distinguish "my enterprise's school" from any other school the user
     *  happens to teach at — see services/classHomeSchool.ts. Server-set only
     *  (pinned immutable in firestore.rules). */
    enterpriseSchoolId?: string;
  };
  settings: {
    theme?: string;
    /**
     * The flashcard pass/fail/almost keys, for THIS teacher, everywhere.
     *
     * Deliberately on the user rather than the class or the deck: the keys are
     * muscle memory, and a teacher running four classes had to re-set them in
     * each — and again in every new class, because a fresh kindergarten class
     * auto-creates a deck with hardcoded q/z.
     *
     * Under `settings` rather than at the top level because `settings` is
     * already on the users-doc allowlist in firestore.rules, so this needs no
     * rules change and no deploy to work. See services/kinderKeys.
     */
    kinderKeys?: import('./services/kinderKeys').KinderKeys;
    /**
     * Gradebook tabs this teacher has hidden from the top bar.
     *
     * A PREFERENCE, never a permission: the toolbar carries thirteen tabs and no
     * teacher uses all of them, so hiding the unused ones makes the used ones
     * easier to hit. It does not change what a teacher may open — Gradebook's
     * permitted-modes check still decides that — so a hidden tab reached another
     * way still renders rather than bouncing. See services/navPreferences.
     *
     * Under `settings` for the same reason as kinderKeys: `settings` is already on
     * the users-doc allowlist in firestore.rules, so this needs no rules change
     * and no deploy.
     */
    hiddenGradebookModes?: string[];
    /**
     * This teacher's own names for gradebook tabs, keyed by mode id.
     *
     * Label only. Nothing keys off it — not the mode id, not the route, not the
     * permission check — so a renamed tab is the same tab to the rest of the app
     * and clearing the name restores the built-in one. See services/navPreferences.
     */
    gradebookTabLabels?: Record<string, string>;
    /**
     * Gradebook display preferences (percentages, colour coding, density).
     *
     * These were four checkboxes that saved nothing and were read by nothing.
     * Note what is NOT here: auto-save. Grades deliberately do not auto-save in
     * this app, so a preference implying they could was removed rather than
     * implemented. See services/displayPrefs.
     */
    display?: import('./services/displayPrefs').DisplayPrefs;
  };
  aiUsage: {
    billingPeriodStart: string; // ISO date "YYYY-MM-DD" — start of current billing period
    count: number;
    /** Lifetime AI credits used DURING a Ultra trial (server-only, never resets).
     *  Trials draw from this small cap instead of the monthly `count`. */
    trialCount?: number;
    /** Referral credits EARNED, lifetime. Server-written; not on the users
     *  allowlist in firestore.rules, so a client cannot mint them. */
    referralGranted?: number;
    /** Referral credits SPENT, lifetime. Available = granted - used. */
    referralUsed?: number;
    /** RETIRED. Lifetime AI credits spent on the free plan under the old scheme.
     *  Free now draws from the MONTHLY `count` pool like every other tier, so nothing
     *  reads this any more — it is kept because it is the only record of what that pool
     *  spent, and because adding it back into any calculation would charge a teacher
     *  today for credits they used last year. See shared/aiCredits.ts. */
    freeCount?: number;
  };
  /** Running total of bytes this teacher has uploaded (stream files + inline
   *  images). Bumped on upload, decremented when a post's files are deleted.
   *  Enforced against PLAN_LIMITS[plan].storageMB at upload time. A nightly
   *  reconcile trues up any drift; this counter is best-effort in the meantime. */
  storageBytesUsed?: number;
  // The teacher's GLOBAL AI marking "brain" — grading guidance applied to ALL of
  // their AI grading (layered under each assignment's own aiGradingInstructions).
  // Read server-side in ai-grade-submission so it applies automatically.
  aiGradingInstructions?: string;
  // Which professional feedback voice the AI marking should adopt. All options are
  // academic and professional; they differ in warmth/directness only. Default 'balanced'.
  // Read server-side (ai-grade-submission) and mapped to a tone directive. Every
  // personality caps written feedback at 1-2 sentences (3 max).
  aiGradingPersonality?: 'balanced' | 'personable' | 'direct' | 'encouraging';
  // Set ONCE, the first time the teacher accepts responsibility for reviewing AI
  // marking/feedback before returning it to students. Immutable after acceptance
  // (the Settings display is read-only). ISO timestamp of acceptance.
  aiGradingResponsibilityAcceptedAt?: string;
  supportStaffEmails?: string[]; // Guidance, admin, etc.
  createdAt: string;
  // One-time dismissal marker for the founder's message (shown once per user).
  // Bump the version number (V1 → V2) if we want to show a new message later.
  seenFounderMessageV1?: string; // ISO timestamp of when user dismissed it
  /**
   * Dismissal stamp for the one-off notice telling Basic subscribers their monthly
   * AI allowance went from 3 to 30. Shown once, not per session: it announces a
   * change rather than nagging about a countdown, so a teacher who has read it
   * should never see it again.
   */
  seenBasicCreditsBumpV1?: string;
  seenFounderMessageV2?: string; // ISO timestamp — the 2.0 "what's new" message
  /** ISO timestamp — the one-time nudge toward the tutorial videos. Same shape
   *  as seenFounderMessageV2 deliberately: one mechanism, not three. */
  seenTutorialPromptV1?: string;
  // Onboarding email delivery tracking — prevents duplicate sends.
  onboardingEmailsSent?: {
    welcome?: string;       // ISO timestamp — Day 0
    gettingStarted?: string; // ISO timestamp — Day 3
    checkIn?: string;        // ISO timestamp — Day 14
  };
  // Teacher XP / leveling (separate from student gamification). See
  // src/services/teacherGamificationService.ts. Cosmetic, no functional gates.
  teacherGamification?: TeacherGamification;
  // Teacher email notification preferences. deadlineReminders is opt-OUT
  // (treated as true when unset) — the report-card deadline-reminder email is
  // suppressed only when this is explicitly false. In-app notifications are
  // unaffected.
  notificationPrefs?: {
    deadlineReminders?: boolean;
    // Opt-OUT (treated as true when unset): email the teacher when a student posts a
    // private message on an assignment submission. Suppressed only when explicitly
    // false. In-app notifications are unaffected.
    studentMessages?: boolean;
    // Opt-OUT (treated as true when unset): the once-daily 4pm digest summarising the
    // work students submitted that day. Suppressed only when explicitly false.
    submissionDigest?: boolean;
  };
  /**
   * Per-class opt-IN to email alerts about classes you CO-TEACH (keyed by classId).
   * Absent or false means no alerts for that class.
   *
   * Opt-in, not opt-out, and per class rather than one global flag: a co-teacher is
   * often a department head, a supply, or a principal auto-attached to every class
   * in the school, and the real need is "the two classes I actually co-teach, not
   * the forty I'm listed on". The class OWNER is unaffected — they're always
   * alerted, and notificationPrefs above stays their off switch.
   *
   * Enforced server-side in netlify/functions/shared/coTeacherAlerts.ts, which
   * every notifier that fans out to a class's teaching team must call.
   */
  coTeacherAlerts?: Record<string, boolean>;
  /**
   * Class settings that follow this teacher into every NEW class.
   *
   * Preferences only — leaderboard, gamification, submissions, due reminders,
   * theme. Never facts about one class (name, code, semester, join code,
   * co-teachers). Applied at creation and never retroactively: changing a
   * preference must not reach back into a class that already exists.
   */
  classDefaults?: import('./services/classDefaults').ClassDefaults;
  /** When the teacher confirmed their preferences on their first class. Unset
   *  means they have not been asked yet. */
  classDefaultsConfirmedAt?: string;
}

/**
 * Teacher XP & leveling. `xp` is the single source of truth; `level` is cached
 * (derived from xp) for cheap reads and a future school leaderboard. Written
 * client-side today (cosmetic, low-stakes); the shape is leaderboard-ready so a
 * server-validated awarder can replace the client path without a data migration.
 */
export interface TeacherGamification {
  xp: number;                 // total lifetime XP
  level: number;              // cached, derived from xp
  currentStreakDays: number;
  longestStreakDays: number;
  lastActiveDate?: string;    // "YYYY-MM-DD" (local) — daily login + streak
  // Per-day per-source counters for anti-farming caps. Reset when `date` rolls.
  daily?: { date: string; bySource: Record<string, number> };
  // Idempotency keys already awarded (pruned to the current day where day-scoped).
  claimedKeys?: string[];
  // Last ~30 XP events for the detail panel.
  recentEvents?: Array<{ source: string; xp: number; at: string; label?: string }>;
  // Streak/level milestones already granted (e.g. "streak-7").
  milestonesAwarded?: string[];
  // One-time flag: existing activity has been folded into starting XP.
  seeded?: boolean;
  // Opt-out: hide the level indicator in the UI.
  hideLevelBar?: boolean;
}

// Plan limits
export const PLAN_LIMITS = {
  /**
   * The free tier for new signups. Use `planLimitsFor` for a specific user:
   * older accounts retain their template/storage limits and unlimited-seat exemptions.
   */
  free: {
    activeClasses: 1,
    // Templates are counted SEPARATELY from activeClasses: a template holds no
    // students and can't be taught, so it isn't an "active class". Counting them
    // together meant one shared template could consume a free teacher's only slot.
    templates: 2,
    // Active students per class; withdrawn rows and staff participants do not count.
    // Publication is coordinated after production grade-privacy verification.
    studentsPerClass: 30,
    /**
     * MONTHLY, like every other plan. Free used to draw from a LIFETIME pool on
     * the grounds that a monthly allowance is "an open cheque against real API
     * spend" for an account anyone can create. The arithmetic does not support
     * that: on Sonnet 5 a report comment is roughly $0.017, so fifteen credits
     * is about eight cents a month per active free account.
     *
     * What a lifetime pool costs instead is the only conversion mechanism there
     * is. Once spent it is permanently spent, the account is dead, and the
     * teacher never comes back. A monthly refresh returns them twelve times a
     * year, each time at the moment they feel the value. Both AI competitors in
     * this market (Gradde, MagicSchool) do it monthly for exactly that reason.
     *
     * Abuse is bounded by the verified-email requirement in evaluateCreditGate,
     * not by making the allowance unrepeatable.
     */
    aiGenerations: 15,
    markSets: 1,
    storageMB: 25,
    archiveCooldownDays: 14,
    archiveRetentionDays: 30, // 30 days before auto-delete
    hasGradebook: true,
    hasStudentHub: true,
    hasAttendance: true,
    /**
     * UNLOCKED. The homepage promises "a gradebook that writes your report card
     * comments" and this flag made that exact screen the one a free signup could
     * not open — the worst possible place to put a wall, and the likeliest reason
     * US traffic was not converting.
     *
     * It also followed the rule this file already set when attendance, seating
     * and gamification were ungated on 2026-10-02: gate what costs money to run,
     * and nothing else. The AI call inside Reports genuinely costs money and is
     * ALREADY metered, per generation, by the credit pool. The view around it —
     * manual comments, averages, the CSV and ARC exports — is computed from data
     * already on the class document and costs nothing. Gating the view charged
     * for the free part to meter the paid part.
     *
     * Pro is not weakened: 150 generations a month against 15, 15 classes against 1,
     * 50 students against 30, and Google Classroom import. (This said "10 for the
     * lifetime of a free account" until the free allowance became monthly a commit
     * later — the comment outlived its own premise by one change.) For any teacher with more than one class, activeClasses
     * is the real gate and always was.
     */
    hasReports: true,
    hasSeating: true,
    hasGamification: true,
    hasClassroomImport: false
  },
  basic: {
    activeClasses: 3,
    templates: 10,
    studentsPerClass: 35,
    /**
     * 30, up from 3. Three credits bought ONE six-to-eight-sentence secondary
     * comment a month, for $4 — less than the free tier, which made Basic
     * strictly worse than free on the thing it was supposed to raise. The
     * paywall copy had already started routing round it, pointing at Pro.
     *
     * Existing subscribers get this immediately rather than at their next
     * renewal: the cap is read per request from this table, and the monthly
     * counter it is compared against is unchanged, so anyone who had spent
     * their three goes straight to 27 remaining. See BasicCreditsBanner, which
     * tells them so.
     */
    aiGenerations: 30,
    markSets: 1,
    storageMB: 100,
    archiveCooldownDays: 7,
    archiveRetentionDays: 90, // 90 days before auto-delete
    hasGradebook: true,
    hasStudentHub: true,
    hasAttendance: true,
    /**
     * Follows free, necessarily. Basic RAISES CAPS ONLY — a paid tier that lacks
     * a feature the free tier has is strictly worse than free, which is not a
     * price point, it is a bug.
     */
    hasReports: true,
    hasSeating: true,
    hasGamification: true,
    hasClassroomImport: false
  },
  pro: {
    activeClasses: 15,
    templates: 25,
    studentsPerClass: 50,
    aiGenerations: 150,
    markSets: 20,
    storageMB: 5000,
    archiveCooldownDays: 3,
    archiveRetentionDays: 180, // 6 months before auto-delete
    hasGradebook: true,
    hasStudentHub: true,
    hasAttendance: true,
    hasReports: true,
    hasSeating: true,
    hasGamification: true,
    hasClassroomImport: true
  },
  ultra: {
    activeClasses: 50,
    templates: 100,
    studentsPerClass: 75,
    aiGenerations: 300,
    markSets: Infinity,
    storageMB: 25000,
    archiveCooldownDays: 0,
    archiveRetentionDays: 365, // 1 year before auto-delete
    hasGradebook: true,
    hasStudentHub: true,
    hasAttendance: true,
    hasReports: true,
    hasSeating: true,
    hasGamification: true,
    hasClassroomImport: true
  },
  // Hidden enterprise tier (assigned via domain capture, not self-serve).
  // Everything Ultra has, but unlimited AI (server enforces a per-user rate
  // limit + org circuit breaker instead of a monthly credit cap). Classes are
  // still capped (unlimited AI is the perk; uncapped class creation is not).
  enterprise: {
    activeClasses: 50,
    templates: 100,
    studentsPerClass: 200,
    aiGenerations: Infinity,
    markSets: Infinity,
    storageMB: 25000,
    archiveCooldownDays: 0,
    archiveRetentionDays: 365,
    hasGradebook: true,
    hasStudentHub: true,
    hasAttendance: true,
    hasReports: true,
    hasSeating: true,
    hasGamification: true,
    hasClassroomImport: true
  }
};

// Plan pricing (in cents for Stripe)
export const PLAN_PRICING = {
  basic: {
    monthly: 400,  // $4/month
    yearly: 4000   // $40/year (save ~17%)
  },
  pro: {
    monthly: 900,  // $9/month
    yearly: 9000   // $90/year (save ~17%)
  },
  ultra: {
    monthly: 1900, // $19/month
    yearly: 19000  // $190/year (save ~17%)
  }
};

// Student type
export interface StudentNote {
  id: string;
  text: string;
  date: string;
  timestamp: number;
}

export interface Student {
  id: string;
  firstName: string;
  lastName: string;
  studentNumber: string;
  preferredName?: string;
  /** True when this "student" is actually a TEACHER participating in the class
   *  (joined by code as a participant — e.g. a PD class or a staff collaboration
   *  space). They use the student experience (submit/post/reply) but are shown
   *  with a "Teacher" badge on the roster and are never synced to the student
   *  directory / OnSIS (a teacher must not become a directory student). */
  isTeacherParticipant?: boolean;
  governmentId?: string;   // OEN — optional, at school board/admin discretion
  aiOptOut?: boolean;       // If true, AI features (report comments, feedback) must not be used for this student
  courseCode?: string; // Course code for split/combined classes
  gcUserId?: string; // Stable Google Classroom user id (roster import) — the reliable dedup key on re-import
  /**
   * Which kindergarten grouping this child belongs to (JK/SK, or whatever the
   * teacher named) — the label's ID, never its text, so renaming a label does
   * not orphan every child who had it. See services/kinderLabels.
   */
  kinderLabelId?: string;
  /**
   * Junior or Senior Kindergarten.
   *
   * Its OWN field rather than one of the four custom labels, because a kinder
   * room is two cohorts and splitting them is not one grouping among several —
   * it is the grouping, needed on nearly every task, and a teacher should not
   * spend two of their four labels on it. The labels stay for whatever else the
   * room splits by (reading groups, arrival year), so a child can be SK AND in
   * the blue reading group, which a single label could never express.
   *
   * Not in STUDENT_PII_FIELDS, matching kinderLabelId: a cohort is not contact
   * information, and the grid, the sort and the printed chart all need it.
   */
  kinderYear?: KinderYear;
  /**
   * This student's name and contact details were replaced by the roster
   * sanitiser. An explicit flag, because the alternative — inferring it from
   * "the name looks like one of our words and there's no email" — is wrong in
   * both directions: a real student with no email is skipped forever, and a
   * real student genuinely called Amber Ash is skipped too.
   */
  pseudonymised?: boolean;
  /**
   * This row's `firstName`/`lastName` are a PLACEHOLDER, not the child's name.
   *
   * The one signal the vault is meant to rest on. Written by `planVaultImport` (roster import into a
   * protected class), by `planLiveConversion` (converting a live class), by the legacy migration's
   * clear step, and by `planSanitise`.
   *
   * It exists because the alternative — inferring it from the row's content, by checking whether both
   * names are words from our own pools and whether the vault disagrees — produced a HIGH review
   * finding four rounds running. Each round tuned the inference for the population the previous round
   * had broken, and each tuning broke a population nobody had listed: migrated classes, imports made
   * before the stamp, rows whose seal had failed, a real child called Jordan Palmer, and finally
   * every class the sanitiser had pseudonymised.
   *
   * A row that carries this needs no inference at all. Rows written before it existed still do, which
   * is what `isPseudonymWord` and the vault-disagrees check are for — and why a backfill that stamps
   * them is the remaining piece of work on this feature.
   */
  placeholderName?: boolean;
  email?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  /** Active parent magic-link token (read-only portal scoped to THIS student in
   *  THIS class). Resolved server-side; regenerating rotates it, revoking clears it. */
  parentLinkToken?: string;
  guidanceEmail?: string; // Guidance counselor or support staff email
  pronouns?: string;
  accommodations?: string;
  notes?: string; // Legacy single note field
  noteHistory?: StudentNote[]; // New timestamped notes system
  /**
   * This roster row belongs to a TEACHER (or admin) account taking the class as
   * a student — professional development, a course they're doing, a colleague's
   * pilot. Legitimate, and it stays in the class roster and gradebook.
   *
   * Stamped at join time from the account's role, which is far stronger than
   * matching emails after the fact. Two uses: the roster shows a badge so a
   * teacher and an admin can see at a glance who is staff, and the school
   * DIRECTORY refuses to roll them up — that feed drives the Students tab,
   * graduation, the credit ledger and the ONSIS export, where a teacher counted
   * as a child is simply wrong.
   */
  isStaffParticipant?: boolean;
  quickAbsences?: number;
  learningSkills?: Record<string, string>;
  /** Stage 0 (readerUids): owner-only reader allow-list, merged in-memory from the
   *  private roster for the owner UI. Stripped before any write to the class doc. */
  authUids?: string[];
  manualAccolades?: ManualAccolade[];
  photoUrl?: string;
  // Student Hub Average - absolute value shown to students in their portal
  // When set, this exact value is shown to the student as their overall grade
  // When undefined, students see their calculated weighted average
  studentHubAverage?: number;
  // Pending Hub Average - teacher's draft value before grade release.
  // Copied to studentHubAverage when grades are released.
  // `null` is the explicit-CLEAR sentinel: on release it clears studentHubAverage
  // (distinct from `undefined` = "no pending change").
  pendingHubAverage?: number | null;
  // Legacy offset field - kept for backwards compatibility migration
  studentHubAverageOffset?: number;
  // Report card comment (persisted)
  reportComment?: string;
  // Teacher-facing "learner profile": an AI-drafted, teacher-editable synthesis of the
  // student's capabilities (strengths, growth areas, work habits, recurring patterns) built
  // from their graded work + feedback. Grounds report-comment generation. Teacher-triggered.
  capabilityProfile?: string;
  capabilityProfileUpdatedAt?: string;
  // Gamification
  xp?: number;
  level?: number;
  badges?: string[]; // Legacy - keeping for backwards compatibility
  // New badge system tracking
  badgeData?: StudentBadgeData;
  // Daily games tracking
  dailyGames?: DailyGamesData;
  // Nickname-only authentication (no Firebase Auth, no email)
  // Used for privacy-compliant classes where students don't create email accounts
  isNicknameOnly?: boolean;
  /**
   * @deprecated The hash now lives in the server-only `classes/{id}/studentAuth/{studentId}`
   * subcollection, NOT in the embedded class doc (classmates could read the embedded array).
   * Kept optional only for the pre-backfill read fallback in login-class-nickname.
   * Teacher UI should use `hasNicknamePassword` to know whether a password is set.
   */
  nicknamePasswordHash?: string;
  /** True when a nickname password is set (safe, non-secret flag for the teacher UI). */
  hasNicknamePassword?: boolean;
  nicknameCreatedAt?: string; // ISO timestamp when nickname account was created
  // Real name for nickname students (teacher-entered, for grading reference)
  realFirstName?: string;
  realLastName?: string;
  // School directory linkage (set when matched to a school directory student)
  schoolStudentId?: string;  // references schools/{schoolId}/students/{id}
  linkedSchoolId?: string;   // the schoolId this student is linked to
  /**
   * Per-class enrolment state. Unset/'active' = normal. 'withdrawn' = parked in
   * the gradebook's Withdrawn section: grades are RETAINED but the student is
   * excluded from the active roster count and class averages. Reversible
   * (reinstate). A hard delete is a separate, irreversible action. For school
   * classes, withdrawing also writes an enrolment EXIT episode (dropped) so the
   * OnSIS record keeps the enter/exit dates.
   */
  status?: 'active' | 'withdrawn';
  withdrawnAt?: string;
  withdrawnReason?: string;
}

/**
 * Sensitive per-student fields, stored in the owner/admin-only
 * `classes/{classId}/private/roster` doc (keyed by studentId) — NOT in the
 * classmate-readable embedded `students[]` array. Access via `studentPrivateOf()`
 * in dataService. Every field is optional so migration can be gradual.
 */
export interface StudentPrivate {
  email?: string;
  governmentId?: string;   // OEN
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  accommodations?: string;
  notes?: string;
  noteHistory?: StudentNote[];
  /** Parent magic-link token — teacher-only; a classmate reading it could open
   *  that child's parent portal, so it lives here, not in the embedded array. */
  parentLinkToken?: string;
  /** Stage 0 (readerUids / A′): the auth uids allowed to read this student's grade
   *  docs. Server-managed (arrayUnion at join/login/claim, cleared on password
   *  reset). Never embedded on the classmate-readable class doc; projected onto each
   *  grade doc's readerUids. */
  authUids?: string[];
  /** Stage 0.5a — teacher-only academic working state. Routed here by
   *  STUDENT_PII_FIELDS: a report-card comment is written ABOUT a student and read
   *  by no classmate, and E/G/S/N learning skills are the same. Declared so the
   *  server-side readers (report-card snapshot, parent portal) are type-checked
   *  against the roster rather than reading them off the stripped class doc. */
  reportComment?: string;
  learningSkills?: Record<string, string>;
  quickAbsences?: number;
  /** Stage 0.5b — teacher-only Hub-average working state.
   *  `studentHubAverageOffset` is the LIVE adjustment (see hubOffsetFor): the mark
   *  the teacher wants, held as a difference from the calculated average so it
   *  tracks as work comes in. It is owner-only for one reason above all — a student
   *  must never be able to see that their mark was adjusted, or by how much. The
   *  adjusted number reaches them as a plain figure in `releasedAverages`.
   *  `pendingHubAverage` is the legacy absolute draft, promoted to the embedded
   *  studentHubAverage at release. */
  pendingHubAverage?: number | null;
  studentHubAverageOffset?: number;
  /** Set only on a copy kept in ClassData.formerStudents after the student LEFT the
   *  class. Their record (incl. grades, by id) sits in limbo so a rejoin by the SAME
   *  email/uid re-associates it; a different new student never inherits it. */
  leftAt?: string;
}

// Gamification Types
export interface AccoladeDefinition {
  id: string;
  title: string;
  description: string;
  badgeLabel?: string;
  icon: string;
  color: string;
  xpReward: number;
  category: 'Performance' | 'Growth' | 'Habits' | 'Engagement' | 'Manual';
}

export interface EarnedAccolade {
  id: string;
  definitionId: string;
  dateEarned: string;
  meta?: string;
  xp: number;
}

export interface GamificationProfile {
  level: number;
  currentXP: number;
  nextLevelXP: number;
  earnedAccolades: EarnedAccolade[];
}

// Manual Accolade
export interface ManualAccolade {
  id: string;
  studentId: string;
  definitionId: string;
  date: string;
  note?: string;
  customXP?: number; // For custom awards, teacher-defined XP amount
}

// Resource link for assessments (supports multiple resources per assessment)
export interface ResourceLink {
  id: string;
  title: string;      // Display name (e.g., "Assignment Instructions")
  url: string;        // The actual URL
  type?: 'document' | 'slides' | 'sheet' | 'form' | 'video' | 'drive' | 'other';
}

// Conditional Release
export type ReleaseConditionType = 'date' | 'grade_threshold' | 'pathway_node' | 'assignment_submitted';

export interface ReleaseCondition {
  type: ReleaseConditionType;
  // date: release on/after this ISO date string
  releaseDate?: string;
  // grade_threshold: student scored >= gradePercent on assessmentId
  assessmentId?: string;
  gradePercent?: number;
  // pathway_node: student completed nodeId in pathwayId
  pathwayId?: string;
  nodeId?: string;
  // assignment_submitted: student submitted to streamPostId (or assessmentId for pathway-linked)
  submissionPostId?: string;
}

// Assessment type
export interface Assessment {
  id: string;
  title: string;
  categoryId: string;
  date?: string;
  outOf: number;
  weight?: number;
  // How the teacher enters/sees the grade in the marking modal for THIS assignment:
  // 'percent' (default) or 'raw' (out of `outOf`). Display/entry only — grades are
  // always stored as a rawScore out of `outOf` regardless. Sticky per-assignment.
  gradeEntryMode?: 'percent' | 'raw';
  type?: 'Diagnostic' | 'Formative' | 'Summative' | 'Portfolio';
  // For Portfolio type: number of submission slots (1–5, teacher-defined)
  portfolioSlots?: number;
  description?: string;
  isHidden?: boolean;
  // If true, this assignment takes no mark — it's posted/tracked but never graded.
  // Excluded from average + category calculations and from "missing work" counts;
  // its gradebook column is display-only (no mark entry).
  isUngraded?: boolean;
  // Culminating task (final project / exam / summative capstone). Radars exclude these by
  // default (formative-progress view); a toggle folds them back in for final reports.
  isCulminating?: boolean;
  // Ungraded-only: when true, this ungraded assignment is NOT shown as a column
  // in the gradebook at all (default is a display-only greyed column).
  ungradedHideFromGradebook?: boolean;
  // Completion tracking: the gradebook cells become a clickable ✓ / ✗ toggle.
  //
  // Two flavours, decided by isUngraded:
  //
  //   UNGRADED — stored as 'Y' / 'N'. Averages ignore it entirely; this is
  //   done/not-done record keeping with no mark attached.
  //
  //   GRADED — stored as an ordinary NUMBER: ✓ writes the assessment's outOf
  //   (full marks) and ✗ writes 0. Deliberately a plain numeric grade rather
  //   than a new kind of value, so every average, report card, export, OnSIS
  //   path and analytics query keeps working with no knowledge of completion
  //   at all. The ✓/✗ is a UI affordance over a normal mark, and a teacher can
  //   still type a number to override it.
  trackCompletion?: boolean;
  markSetId?: string;
  rubricId?: string;
  // Embedded rubric data for grading (optional - only present if rubric is attached)
  rubric?: Rubric;
  // New fields
  dueDate?: string;
  link?: string;  // Single link (kept for backwards compatibility)
  resources?: ResourceLink[];  // Multiple resource links with titles
  /** Google Classroom add-on attachments (Practice Sets, interactive videos) that
   *  the API never sends us, inferred from the teacher's own instructions at import
   *  time. Drives the "needs rebuilding" checklist — see services/importGaps. */
  importGaps?: import('./services/importGaps').ImportGap[];

  instructions?: string;
  streamPostId?: string;
  isDraft?: boolean;
  gcSourceId?: string;    // Google Classroom source item id — for re-import dedup
  originSourceId?: string; // Stable GC-origin id that PERSISTS through template creation/duplication (gcSourceId gets stripped to unlink). Used ALONGSIDE gcSourceId for dedup so re-importing an item never duplicates it, regardless of source.
  gradesPushedAt?: string; // ISO timestamp of the last successful grade push to Google Classroom
  // studentId → the rawScore that was last pushed to Classroom for this assessment.
  // Lets the push button show how many marks are NEW/changed since the last push
  // ("3 new to push") instead of blindly re-offering every mark. A student whose
  // current mark differs from (or is absent from) this map is "to push".
  pushedScores?: Record<string, string | number>;
  // ISO timestamp of the last teacher-triggered "grades posted" email notification
  // (notify-grades-posted). Lets the UI show "notified" and is safe to re-run.
  gradeNotifiedAt?: string;
  // Student ids already emailed a "grades posted" notice for this assessment.
  // "Notify again" re-emails ONLY students newly released since (not in this set).
  gradeNotifiedStudentIds?: string[];
  // Multi-category support: categoryId -> weight (1-5)
  // If an assessment spans multiple categories, this maps each categoryId to its weight
  // Higher weight = counts more within that category (default 1)
  categoryWeights?: Record<string, number>;
  // Bundle ID for linking multi-category assessments (e.g., "Book Talk" with Thinking + Communication)
  // All assessments with the same bundleId are treated as ONE assignment with multiple grade components
  bundleId?: string;
  // The display title for bundled assessments (without category suffix)
  // e.g., "Book Talk" instead of "Book Talk (Thinking)"
  bundleTitle?: string;
  // Per-assignment submission toggle - if false, students cannot submit to this assignment
  acceptingSubmissions?: boolean;
  // If true, students must provide a self-assessment when submitting
  requireSelfAssessment?: boolean;
  // If true, this assessment was created from the Class Hub stream (for deletion tracking)
  createdFromStream?: boolean;
  // Conditional release — if set, students only see this assessment when condition is met
  releaseCondition?: ReleaseCondition;
  // Curriculum expectations linked to this assessment
  curriculumExpectations?: CurriculumExpectation[];
  // ── Math assessments ────────────────────────────────────────────
  // 'math' marks this as a math assessment: AI grading reads the student's
  // mathematical work (typed, handwritten photos, drawn graphs/figures) and
  // evaluates the METHOD, not just the final answer. Absent = language/general.
  subject?: 'math';
  // Grade the working/process (partial credit for correct method even if the
  // final answer is wrong). Defaults ON for math; prominently toggleable.
  gradeProcess?: boolean;
  // Free-text steering for the AI grader, layered on top of the
  // rubric + instructions (e.g. "weight originality", "flag possible
  // AI-generated text", "be generous on conventions for ELLs").
  aiGradingInstructions?: string;
}

export interface SubmissionFile {
  fileUrl: string;   // download-token URL
  filePath: string;  // Storage path (for scheduled purge)
  fileName: string;
  fileType: string;  // MIME type
}

// One private message in a submission's teacher↔student thread.
export interface SubmissionMessage {
  id: string;
  authorRole: 'teacher' | 'student';
  authorName: string;
  content: string;
  createdAt: string;  // ISO
}

export interface Submission {
  id: string;
  postId: string;           // Which assignment post this is for
  assessmentId?: string;    // Link to gradebook assessment (if linked)
  studentId: string;        // Primary student who submitted
  studentName: string;      // For display
  groupMemberIds?: string[]; // Additional group members (if group assignment)
  groupMemberNames?: string[]; // Names of group members for display
  link: string;             // Google Drive URL (just a string! — also used as first portfolio link)
  portfolioLinks?: string[]; // Additional portfolio submission links (slots 2–5, for Portfolio type)
  // Uploaded file (Ultra written-work grading): hosted in Firebase Storage so
  // AI can grade it directly (PDF/vision). fileUrl is a download-token URL.
  // The single fileUrl/fileName/etc. are kept for back-compat + mirror files[0];
  // `files` holds ALL uploaded files when a student submits more than one.
  fileUrl?: string;
  filePath?: string;        // Storage path (for scheduled purge)
  fileName?: string;
  fileType?: string;        // MIME type
  files?: SubmissionFile[]; // Multiple uploaded files (files[0] mirrors the above)
  submittedAt: string;      // When they submitted
  studentComment?: string;  // Optional private note from the student to the teacher (not shown to the class)
  /**
   * Private back-and-forth between the teacher and THIS student about their
   * submission. The only messaging surface in the app.
   *
   * NOT CLASS-VISIBLE, and that is now enforced rather than asserted: the messages
   * live in `classes/{id}/submissionPrivate/{submissionId}`, readable only by the
   * uids on that doc's allow-list. This field is hydrated back for the teacher and
   * for the student's own row; it should be absent on a migrated class doc, where
   * it was readable by all thirty classmates.
   */
  thread?: SubmissionMessage[];
  /**
   * Non-content signal that a conversation exists, kept ON the row so the teacher's
   * notification bell — which reads raw class docs without hydration — still works
   * once the messages have moved off. A timestamp and a role, never any words.
   */
  threadUpdatedAt?: string;
  threadLastRole?: 'teacher' | 'student';
  grade?: number;           // Teacher's grade (e.g., 85)
  feedback?: string;        // Teacher's private feedback text
  gradedAt?: string;        // When graded
  gradedBy?: string;        // Teacher ID
  // Teacher-created placeholder to leave feedback about MISSING work — the student
  // hasn't actually submitted. Non-blocking: they can still submit (which replaces
  // this), and they see the feedback as a nudge. Ignored in "submitted" counts.
  noSubmission?: boolean;
  // Student self-assessment (optional - only if assignment requires it)
  selfAssessment?: {
    expectedGrade: number;    // Student's expected grade (0-100 or out of assignment total)
    reflection?: string;      // Optional reflection text
  };
  // ── Imported from Google Classroom (submission sync) ───────────────────────
  // Set when this submission was pulled from Classroom rather than submitted in LUC.
  // The attached work opens as a Drive link (Google doesn't share file content).
  // gcSubmissionId + updateTime drive incremental dedup on re-sync; a re-sync updates
  // links/timestamp in place and NEVER clobbers a teacher's grade/feedback, and never
  // overwrites a LUC-native submission for the same student+assignment.
  /**
   * This hand-in is covered by another student's group submission.
   *
   * Set when a teacher groups partners who handed in SEPARATELY (Google
   * Classroom has no group submission, so they always do). The row stays —
   * deleting it would destroy the private teacher-student thread on it, any
   * mark already given, the self-assessment, and the Classroom link, which
   * would also let the next sync recreate the row and silently undo the
   * grouping. Lists hide a covered row; everything that COUNTS submissions
   * still sees it, which is correct, because that student did submit.
   */
  coveredByGroupSubmissionId?: string;
  importedFromGC?: boolean;
  gcSubmissionId?: string;    // Classroom studentSubmission id
  gcUpdateTime?: string;      // Classroom updateTime last synced (skip if unchanged)
  // Teacher manually dismissed this from the activity log (e.g. a spurious
  // "needs marking" entry from an unsubmit/resubmit on Google Classroom that
  // doesn't actually need re-assessment). Hidden from the activity feed only;
  // the submission + any grade are untouched.
  hiddenFromActivity?: boolean;
}

// Grade type
export interface Grade {
  studentId: string;
  assessmentId: string;
  rawScore: string | number;
  feedback?: string;
  submittedAt?: string;
  // Rubric-based grading - stores selected level for each criterion
  rubricScores?: Record<string, number>; // criterionIndex -> levelIndex
}

// Rubric types for structured grading
export interface RubricCell {
  descriptor: string;
}

export interface Rubric {
  id: string;
  title: string;
  criteria: string[]; // Array of criterion names (e.g., "Content", "Organization", "Language")
  levels: string[]; // Array of level names (e.g., "Level 4", "Level 3", "Level 2", "Level 1")
  levelPoints?: number[]; // Points for each level (e.g., [4, 3, 2, 1] or [100, 75, 50, 25])
  cells: Record<string, RubricCell>; // key: "criteriaIndex-levelIndex" -> descriptor
  createdAt: string;
  ownerId?: string;
}

// Grade History Entry - tracks all grade changes
export interface GradeHistoryEntry {
  id: string;
  studentId: string;
  assessmentId: string;
  previousScore: string | number | null; // null if first entry
  newScore: string | number;
  previousFeedback?: string;
  newFeedback?: string;
  changedAt: string; // ISO timestamp
  changedBy: 'teacher' | 'system'; // 'system' for auto-imports, submission sync, etc.
  /**
   * WHICH teacher changed it. `changedBy` only records the KIND of actor, which was
   * fine when a class had exactly one teacher — but co-teachers exist now, so
   * "Teacher" alone can't answer "who changed this mark?". Required for a real audit
   * trail once marks are a system of record. Absent on entries written before this
   * existed, and on 'system' entries.
   */
  changedByUid?: string;
  /** Display name at the time of the change (names change; the record shouldn't). */
  changedByName?: string;
  reason?: string; // Optional reason for change (e.g., "Regrade request", "Late submission")
}

/**
 * One CHANGE to a student's attendance mark on one date. Attendance writes replace
 * the whole day wholesale, so without this an edit (A → P) leaves no trace of what
 * the mark was, who changed it, or when — which is exactly what an attendance
 * inspection asks for. Written to the `attendanceHistory` SUBCOLLECTION (never
 * embedded on the class doc — same reasoning as gradeHistory: the 1 MiB doc limit).
 * Only actual changes are recorded; re-saving an unchanged day writes nothing.
 */
export interface AttendanceHistoryEntry {
  id: string;
  studentId: string;
  date: string;                 // the attendance date being changed (YYYY-MM-DD)
  previousMark: 'P' | 'A' | 'L' | 'E' | null;  // null = no mark before (first entry)
  newMark: 'P' | 'A' | 'L' | 'E' | null;       // null = mark removed
  previousLateTime?: string;
  newLateTime?: string;
  previousNote?: string;
  newNote?: string;
  changedAt: string;            // ISO timestamp
  changedByUid?: string;        // WHO changed it (co-teachers + office staff all can)
  changedByName?: string;       // display name at the time of the change
}

// Mark Set Category
/** What a mark-set category covers in the curriculum. */
export interface CategoryCurriculum {
  /** The document and course the selection was made against — a code like "3.2"
   *  means nothing without them. */
  filename: string;
  courseCode: string;
  courseTitle?: string;
  /** The chosen overall expectations, denormalised so the mark set can be read
   *  and fed to the AI without fetching the curriculum file again. */
  expectations: CurriculumExpectation[];
}

export interface MarkSetCategory {
  id: string;
  name: string;
  weight: number;
  color: string;
  /** Teacher-authored, one or two sentences on what this category actually assesses.
   *  Feeds the AI for report comments, AI marking, and the AI Tutor — the more specific
   *  and concise, the better the AI's output. Optional. */
  description?: string;
  /**
   * Curriculum expectations this category covers.
   *
   * Many-to-one on purpose: an English category routinely spans a whole strand,
   * and a teacher may want a couple of expectations from elsewhere too. One
   * category per strand would not survive contact with a real mark book, which
   * also carries Exam, Culminating and Attendance — categories with no
   * curriculum behind them at all.
   *
   * Held on the CATEGORY rather than linking the mark set to a document,
   * because mark sets are reused: the same set serves ENG3U and ENG4U. A
   * selection names the course it came from and is simply ignored where it does
   * not apply, instead of forcing one mark set per curriculum.
   */
  curriculum?: CategoryCurriculum;
}

// Category alias for compatibility
export type Category = MarkSetCategory;

/**
 * ── Grade output SCALE (display layer only) ──────────────────────────────────
 * The computed average is ALWAYS a percent internally (the source of truth). A
 * GradeScale is a DISPLAY transform layered on top at render sites — it never
 * changes any grading math, and `undefined` (or type:'percent') reproduces
 * today's exact "NN%" behaviour.
 *
 *  - 'percent' — show "NN%" (default; bands ignored).
 *  - 'letter'  — show the band label whose min% <= the percent (e.g. "A", "B").
 *  - 'gpa'     — show the band's `gpa` number formatted (e.g. "3.7").
 *  - 'levels'  — show the band label (e.g. "Level 4", "Exceeding").
 *
 * `bands` are sorted DESCENDING by `min` (inclusive percent floor). When absent,
 * formatGrade() falls back to sensible per-type defaults (see utils.ts).
 */
export interface GradeScaleBand {
  min: number;     // inclusive percent floor for this band
  label: string;   // what shows (e.g. "A", "4", "Level 4", "Exceeding")
  gpa?: number;    // only meaningful for the 'gpa' type
}

export interface GradeScale {
  type: 'percent' | 'letter' | 'gpa' | 'levels';
  bands?: GradeScaleBand[];
}

// Mark Set
export interface MarkSet {
  id: string;
  ownerId: string;
  name: string;
  categories: MarkSetCategory[];
  /**
   * Optional default output scale seeded onto a class when this mark set is
   * chosen (e.g. an Ontario "Levels" preset carries a levels scale). Copied to
   * ClassData.gradeScale at class creation; the class value always wins after.
   */
  defaultScale?: GradeScale;
}

// Attendance
export interface AttendanceDay {
  date: string;
  records: Record<string, 'P' | 'A' | 'L' | 'E'>;
  lateTimes?: Record<string, string>;
  notes?: Record<string, string>;  // per-student notes/reasons (e.g. "doctor's appointment")
}

// Seating Chart
export interface SeatingChart {
  id?: string;
  name?: string;
  rows: number;
  cols: number;
  positions: Record<string, { row: number; col: number } | null>;
  /** Free-form (drag-anywhere) layout coordinates, keyed by studentId. */
  freeformPositions?: Record<string, { x: number; y: number }>;
  /**
   * Grid cells the teacher has removed, as "row,col".
   *
   * Real rooms are not rectangles: a back row of four against a front row of
   * six, a gap where the door opens, a corner the projector cart lives in.
   * Without this a grid can only be rows x cols, so a teacher with uneven rows
   * has to pad the chart with desks that are not there.
   */
  removedSeats?: string[];
  seats?: Record<string, string | null>; // Legacy support
  updatedAt?: string;
}

/**
 * A declarative, safe spec for an embedded graph/diagram (rendered by JSXGraph).
 * Purely data — no code — so it's safe to store, generate, and print. Function
 * terms are compiled by JSXGraph's sandboxed parser (JessieCode), never eval.
 */
export interface MathGraphSpec {
  boundingBox?: [number, number, number, number]; // [xMin, yMax, xMax, yMin]; default [-6,6,6,-6]
  axis?: boolean;                                   // show x/y axes (default true)
  functions?: string[];                             // e.g. ["x^2", "2*x+1", "sin(x)"]
  points?: { x: number; y: number; label?: string }[];
  segments?: { from: [number, number]; to: [number, number] }[];
  lines?: { through: [[number, number], [number, number]] }[];
  circles?: { center: [number, number]; radius: number }[];
  polygons?: { vertices: [number, number][] }[];
}

/**
 * An interactive "plot the answer" question. The student drags points on a grid;
 * their construction is graded against a target. Deterministic + pure (no CAS).
 *  - mode 'points': plot `pointCount` points; graded against `targetPoints` (set match).
 *  - mode 'line':   drag two points that define a line; graded against `targetLine`.
 * Student points snap to the integer grid, so grading is clean.
 */
export interface GraphInputSpec {
  mode: 'points' | 'line';
  boundingBox?: [number, number, number, number]; // [xMin, yMax, xMax, yMin]; default [-6,6,6,-6]
  tolerance?: number;                               // grid-unit slack (default 0.25)
  pointCount?: number;                              // points mode
  targetPoints?: { x: number; y: number }[];        // points mode answer key
  targetLine?: { slope: number; intercept: number };// line mode answer key (y = mx + b)
  startPoints?: { x: number; y: number }[];         // where the draggable points begin
}

// Pop Quiz types
// mc = single-correct multiple choice; tf = true/false; multi = "pick N" (several correct);
// match = matching pairs; short = written/short answer (AI-assisted or manual grading).
// 'handwritten' = the student answers by HAND — sketching on a canvas or attaching a
// photo of work done on paper. The submitted answer is an image, so it is never
// auto-graded: it routes to the same manual/AI-assisted marking flow as 'short'.
export type PopQuizQuestionType = 'mc' | 'tf' | 'multi' | 'match' | 'short' | 'numeric' | 'expression' | 'graphinput' | 'handwritten';

/**
 * A student's answer to one question, shape depends on the question type:
 *   mc / tf   -> number (selected option index)
 *   multi     -> number[] (selected option indices)
 *   match     -> Record<leftIndex, rightIndex> (the student's pairing)
 *   short     -> string (free text)
 */
export type PopQuizAnswerValue = number | number[] | Record<string, number> | string;

/** One matching pair; correct pairing is left[i] ↔ right[i] (right side shuffled at render). */
export interface PopQuizMatchPair {
  left: string;
  right: string;
}

/**
 * A single alternate phrasing of a question that tests the SAME concept at the
 * same difficulty, with its own answer key. Used for per-student test variants.
 */
export interface PopQuizVariant {
  id: string;
  questionText: string;
  options: string[];
  correctIndex: number;
  // Per-type answer keys (a variant keeps its master's TYPE; only the surface changes).
  correctIndices?: number[];      // multi
  matchPairs?: PopQuizMatchPair[]; // match
  matchScramble?: number[];        // match (private): the permutation used to scramble the class-doc rights; hydrated for grading, never on the student copy
  matchScrambled?: boolean;        // match (class doc): marks that matchPairs' rights are scrambled (answer withheld)
  modelAnswer?: string;            // short (reference answer)
  answerValue?: string;            // numeric / expression (variant changes the numbers → its own key)
  equivalenceMode?: 'equivalent' | 'exact'; // expression
  tolerance?: number;
  tolerancePercent?: number;
  acceptedForms?: string[];
  source?: 'manual' | 'ai'; // how the variant was authored (for the editor UI)
}

export interface PopQuizQuestion {
  id: string;
  type: PopQuizQuestionType;
  questionText: string;
  options: string[]; // 4 options for MC, 2 for TF
  correctIndex: number; // Index of correct option (mc/tf). For other types see below.
  correctIndices?: number[];        // multi: indices of all correct options
  matchPairs?: PopQuizMatchPair[];  // match: correct left↔right pairs
  matchScramble?: number[];         // match (private): permutation scrambling the class-doc rights; hydrated for grading, never on the student copy
  matchScrambled?: boolean;         // match (class doc): marks matchPairs' rights as scrambled (answer withheld from students)
  modelAnswer?: string;             // short: optional reference answer for grading
  // numeric: the student types a value; auto-graded by numeric comparison (no AI, no credits).
  // answerValue = canonical correct answer as a string ("42", "3/4", "-2.5", "1.5e3", "1 1/2").
  // tolerance = absolute; tolerancePercent = relative %; default = exact. acceptedForms = extra
  // exact strings to accept. unit = display-only label shown beside the input.
  // expression: the student types an algebraic expression/equation; auto-graded by
  // a computer-algebra system (compute-engine) so ANY equivalent form is accepted
  // (e.g. "2(x+2)" === "2x+4"). answerValue holds the canonical answer; equivalenceMode
  // 'equivalent' (default) accepts equivalent forms, 'exact' requires the same written
  // form. acceptedForms are extra literal strings to accept without CAS.
  answerValue?: string;
  equivalenceMode?: 'equivalent' | 'exact';
  tolerance?: number;
  tolerancePercent?: number;
  acceptedForms?: string[];
  unit?: string;
  categoryId?: string; // If graded, which mark set category this question belongs to
  points: number; // Points for this question (for graded quizzes)
  // --- Per-student variants ---
  // foundational = every student sees this question verbatim (the original).
  // When false/undefined AND `variants` is non-empty, each student is
  // deterministically assigned one option from the pool [original, ...variants].
  isFoundational?: boolean;
  variants?: PopQuizVariant[];
  // Runtime-only: set by resolveQuizForStudent to record which phrasing a
  // student was shown. Never persisted on the master quiz.
  resolvedVariantId?: string;
  // Optional per-question time limit (seconds). Overrides the quiz-wide
  // secondsPerQuestion. When it expires the question locks and advances.
  timeLimitSeconds?: number;
  // Written-answer sizing (type 'short'). isLong marks a paragraph/essay-style
  // "long answer" (bigger box, higher default word limit); both still grade as
  // written answers (AI/manual). wordLimit caps the response (default 100 short /
  // 500 long); enforced + shown to the student in the taker.
  isLong?: boolean;
  wordLimit?: number;
  // Optional embedded graph/diagram shown beneath the question text (any type).
  graph?: MathGraphSpec;
  // For type 'graphinput': the interactive plot-the-answer spec + its answer key.
  graphInput?: GraphInputSpec;
  // When true, the student is asked to SHOW THEIR WORK (steps/method) alongside
  // their answer. Captured for the teacher (and process/method credit); does not
  // change how the final answer is auto-graded. Best on numeric/expression/mc math.
  requireWork?: boolean;
  // --- Multi-part questions (presentation grouping ONLY) ---
  // A multi-part problem — "Given f(x)=2x+3: (a) find f(5)  (b) solve f(x)=0" — is
  // stored as several INDEPENDENT questions that share a `groupId`. Each part keeps
  // its own type, points and answer key and is graded exactly like a standalone
  // question, so the scoring path (quizScoring.ts) never has to recurse. Grouping is
  // display-only: the taker/results/print show the shared stem once and label parts
  // a, b, c. `groupStem` (the shared preamble) is carried on EVERY part so each
  // paginated screen and printout can render it without a lookup. `partLabel` is the
  // part's letter within the group.
  groupId?: string;
  groupStem?: string;
  partLabel?: string;
  // --- Video questions ---
  // "Question 3 is a video with five embedded questions worth a mark each" is
  // modelled as a multi-part GROUP: independent questions sharing a groupId, each
  // with its own type, points and answer key, each graded standalone. The group
  // occupies one slot in the test and its marks are simply the sum of its parts —
  // which falls out of the existing model rather than needing new scoring.
  //
  // Deliberately NOT a new container type holding child questions. That would
  // give quizScoring a second shape to recurse into, and every consumer —
  // marking, results, printing, variants, sections, answer-key verification —
  // would need to learn about it. Reusing groupId means none of them change.
  //
  // `groupVideoUrl` is carried on EVERY part, for the same reason groupStem is:
  // each paginated screen and printout renders without a lookup.
  groupVideoUrl?: string;
  // Seconds into the group's video at which THIS part is asked. The player pauses
  // here and shows it. Parts sort by this within the group.
  videoAtSeconds?: number;
}

/**
 * A named group of questions with its own optional time limit. Sections are
 * OPTIONAL — a quiz with no sections is a flat list (legacy behaviour).
 * `questionIds` reference PopQuizQuestion.id, in presentation order.
 */
export interface PopQuizSection {
  id: string;
  title: string;
  timeLimitSeconds?: number; // optional per-section limit; expiry advances past the section
  categoryId?: string; // when graded per-section: the mark set category this section counts toward
  questionIds: string[];
  // When true, the ORDER of this section's questions is shuffled per student
  // (deterministically): the SAME questions, in a different order, so a student
  // glancing at a neighbour's screen can't tell where they are. Does NOT change
  // which questions are shown (that is per-student variants) — only their order.
  shuffleQuestions?: boolean;
}

/**
 * Soft-proctoring configuration. NO webcam / NO live monitoring — every signal
 * is recorded during the attempt and surfaced to the teacher afterwards.
 * Each toggle defaults ON when a teacher opens the settings; absent object =
 * legacy quiz with no proctoring.
 */
export interface PopQuizProctorSettings {
  trackTimePerQuestion: boolean; // record seconds spent on each question (heatmap)
  trackFocusLoss: boolean;       // count tab/window switches + time away
  blockCopyPaste: boolean;       // disable copy/paste/right-click; count blocked paste attempts
  requireFullscreen: boolean;    // request fullscreen on start; count exits
}

/**
 * 'pop-quiz' = short, gamified, instant-feedback quiz (the original).
 * 'test'     = longer assessment: results withheld until released, gamification
 *              optional, richer question types (added incrementally).
 * The two share one data model + taker engine; `kind` flips defaults + chrome.
 */
export type PopQuizKind = 'pop-quiz' | 'test' | 'interactive-video';

/**
 * INTERACTIVE VIDEO — a quiz whose questions are pinned to points in a video.
 *
 * Deliberately a PopQuiz `kind` rather than a parallel feature: every question
 * type, the variant/shuffle machinery, availability windows, and the whole grading
 * and gradebook-writing spine already exist and are tested. What a video adds is
 * WHEN a question appears and what the player does around it.
 *
 * This also exists because Google Classroom add-ons (interactive video, Practice
 * Sets) are absent from Google's API entirely — a teacher migrating a course
 * cannot bring them across at all, and the rebuild checklist can only point at the
 * hole. This is what fills it.
 */
export interface InteractiveVideoCue {
  id: string;
  /** Seconds into the video at which playback pauses and the question appears. */
  atSeconds: number;
  /** References a PopQuizQuestion.id in the quiz's `questions` array. */
  questionId: string;
}

export interface InteractiveVideoConfig {
  /** Only YouTube today; named so a Drive/Vimeo source can be added without a
   *  migration, since teachers attach video every one of those ways. */
  provider: 'youtube';
  /** The 11-char YouTube id, NOT a URL — parsed once at authoring time so the
   *  player never has to guess at watch/embed/youtu.be/shorts URL shapes. */
  videoId: string;
  videoTitle?: string;
  /** Total length in seconds when known; used to validate cue timestamps. */
  durationSeconds?: number;
  cues: InteractiveVideoCue[];

  /** What it takes to get past a question.
   *   'answer'  — answer it, right or wrong, and carry on (default)
   *   'correct' — must be right before the video resumes
   *   'explain' — must be right, and gets the explanation before resuming */
  progression: 'answer' | 'correct' | 'explain';

  /** 'immediate' shows right/wrong at the question (default). 'end' withholds it
   *  until the video finishes, and is only coherent with 'answer' progression —
   *  the other two modes have to tell the student they were wrong to gate them. */
  feedbackTiming: 'immediate' | 'end';

  /** What lands in the gradebook.
   *   'completion' — watched it and answered everything (default; this is how
   *                  teachers actually use these: as attendance, not as a test)
   *   'accuracy'   — percent correct
   *   'threshold'  — full marks at or above `thresholdPercent`, zero below */
  gradingBasis: 'completion' | 'accuracy' | 'threshold';
  thresholdPercent?: number;

  /** Retries are for learning; the grade records the first attempt. Separating
   *  these is what lets a student re-try freely without gaming the mark. */
  scoreFirstAttemptOnly: boolean;
  allowUnlimitedAttempts: boolean;
  /** Stop students from dragging past the questions. */
  scrubLocked: boolean;
  /** Once they've been through it, let them scrub back to re-watch. */
  unlockScrubOnRetry: boolean;
}

// Preset display labels a teacher can attach to a quiz/test (what students SEE, e.g.
// "Final Exam"). Purely cosmetic — `kind` still drives behaviour (proctoring, results
// withholding, gamification, gradebook). 'test'-kind quizzes get the exam-ish labels.
export const POP_QUIZ_LABELS = ['Pop Quiz', 'Quiz', 'Test', 'Unit Test', 'Midterm', 'Exam', 'Final Exam', 'Practice Test'] as const;

export interface PopQuiz {
  id: string;
  title: string;
  topic: string; // Topic/subject matter
  kind?: PopQuizKind; // default 'pop-quiz'
  /** What students see this called (e.g. "Final Exam"). Cosmetic only; falls back to
   *  'Test'/'Pop Quiz' from `kind` when unset. */
  displayLabel?: string;
  showGamification?: boolean; // default true; when false, taker hides XP/celebration
  resultsReleased?: boolean; // tests withhold score/grade from students until true
  resultsReleasedAt?: string;
  questions: PopQuizQuestion[];
  /** Present only when `kind === 'interactive-video'`. */
  video?: InteractiveVideoConfig;
  xpPerQuestion: number; // 5-10 XP awarded per correct answer
  participationXP: number; // Base XP just for taking the quiz (e.g., 10 XP)
  // Timing
  timed: boolean; // Whether quiz is timed
  secondsPerQuestion?: number; // If timed, default seconds per question (per-question override on the question)
  totalTimeLimitSeconds?: number; // Optional whole-test timer; expiry auto-submits
  sections?: PopQuizSection[]; // Optional named sections with their own limits
  // For a section-less quiz (flat `questions` list): shuffle the ORDER of the
  // questions per student (deterministically) — the SAME questions in a different
  // order. Ignored when the quiz has sections (use PopQuizSection.shuffleQuestions
  // per section instead). Does NOT change which questions are shown.
  shuffleQuestionOrder?: boolean;
  proctorSettings?: PopQuizProctorSettings; // Optional soft-proctoring config
  // Availability
  availableFrom?: string; // ISO datetime when quiz becomes available
  availableUntil?: string; // ISO datetime when quiz closes
  durationDays?: number; // Alternative: 1-2 days from posting
  // Manual teacher gate: when set to false, students cannot start regardless of
  // the date window (a held exam the teacher opens with an "Open now" button).
  // Undefined/true means not manually held. Enforced client + server-side.
  manuallyReleased?: boolean;
  // ISO timestamp of the most recent teacher re-hold (manuallyReleased flipped to
  // false). Lets the availability gate grandfather students who STARTED before the
  // re-hold while blocking anyone who starts at/after it. Cleared when re-opened.
  heldAt?: string;
  // Grading
  isGraded: boolean; // Whether results affect gradebook
  // How graded questions map to gradebook categories:
  //   'single'  (default) — whole test counts toward gradingCategoryId (one assessment)
  //   'section' — each section counts toward its own category (one assessment per category)
  //   'question'— each question carries its own category (one assessment per category)
  // The resolved per-question category is always stamped onto PopQuizQuestion.categoryId,
  // so downstream grade-writing is uniform regardless of mode.
  gradingMode?: 'single' | 'section' | 'question';
  gradingCategoryId?: string; // Mark set category for the gradebook entry (single mode / fallback)
  assessmentIds?: string[]; // If graded, links to assessment(s) created for this quiz (one per category)
  subject?: 'math'; // Math test — short answers grade the working (math-aware AI)
  // When true, students get an optional per-question DRAWING pad ("sketch your
  // work") — freehand math working, diagrams, number lines. Each sketch a student
  // makes is uploaded to Firebase Storage and its URL attached to the attempt
  // (PopQuizAttempt.drawings) so the teacher sees it. Defaults on for math tests.
  allowDrawing?: boolean;
  // Test-level AI grading guidance for written (short/long) answers. Free text —
  // e.g. "accept any two valid reasons", "reward method over the final number".
  // Applied to EVERY written question's AI grade on this test, merged UNDER the
  // teacher's global marking guidance (this test's guidance wins on conflict), on
  // top of each question's own model answer.
  aiGradingInstructions?: string;
  // Per-student RE-WRITE grants: the teacher lets a student retake this test. Each
  // grant permits ONE fresh attempt (bypassing the availability window, even if
  // the test has closed) and is "consumed" once the student submits an attempt
  // completed at/after grantedAt. Re-granting (a later grantedAt) reopens it once.
  //   policy 'replace' — the new attempt's mark counts.
  //   policy 'highest' — the higher of the original and re-write marks counts.
  retakeGrants?: Record<string, PopQuizRetakeGrant>; // studentId -> grant
  // Tracking
  createdAt: string;
  postedAt?: string; // When it was posted to stream
  streamPostId?: string; // Link to stream post
  createdBy: string; // Teacher ID
}

export interface PopQuizRetakeGrant {
  grantedAt: string;                 // ISO, informational (display/ordering only)
  policy: 'replace' | 'highest';     // which mark counts once both attempts exist
  // The student's attempt COUNT at grant time. Consumption is by count, not by
  // clock: the grant is unconsumed while the student's attempt count is still
  // <= attemptsAtGrant, and consumed once they submit one more. Count-based so a
  // teacher/server clock skew can never permit >1 extra attempt or dead-grant.
  attemptsAtGrant: number;
}

export interface PopQuizAttempt {
  id: string;
  // Stable client-generated id for the whole attempt (survives resume). Lets the
  // server dedup a resubmit when the first submit succeeded but its response was
  // lost (flaky/locked-down network) — an idempotent retry instead of a spurious
  // "already submitted". Undefined on legacy attempts.
  clientAttemptId?: string;
  quizId: string;
  studentId: string;
  studentName: string;
  // True when the teacher entered/submitted this attempt on the student's behalf
  // (rescue for a student who couldn't submit). Never set by the student path.
  submittedByTeacher?: boolean;
  answers: Record<string, PopQuizAnswerValue>; // questionId -> answer (shape per question type)
  // "Show your work" text, questionId -> the student's steps/method. Captured for
  // the teacher; does not affect auto-grading of the final answer.
  work?: Record<string, string>;
  // The student's global scratchpad (one box spanning the whole quiz). Captured for
  // the teacher so their overall working is visible even on MC questions.
  notes?: string;
  // Freehand DRAWING work (when the quiz has allowDrawing): questionId -> the
  // uploaded sketch's download URL, shown to the teacher in results. drawingPaths
  // keeps the Storage path per question for year-end cleanup (mirrors submissions).
  drawings?: Record<string, string>;
  drawingPaths?: Record<string, string>;
  score: number; // Auto-graded points earned (fractional with partial credit)
  totalQuestions: number;
  // Per-question earned fraction (0..1), for partial credit + results display.
  questionScores?: Record<string, number>;
  // Short-answer text needing AI/manual grading, plus the teacher's awarded marks.
  pendingManualGrade?: boolean; // true if any short-answer awaits grading
  xpEarned: number;
  /** Interactive video only: the attempt's own outcome, which per-question
   *  re-grading cannot reconstruct. `videoCompleted` is the difference between a
   *  finished video and an abandoned one, and completion/pass-mark grading are
   *  statements about the attempt as a whole rather than about any question. */
  videoCompleted?: boolean;
  videoCorrect?: number;
  videoTotal?: number;
  xpApplied?: boolean; // true once xpEarned has been added to the student's XP (idempotency)
  // Teacher rescue markers (a student who couldn't submit): the teacher submitted
  // their autosaved work or entered a mark directly. directMark = score IS the mark
  // (no per-question grid); rescuedByTeacher = built by the teacher, not the student.
  rescuedByTeacher?: boolean;
  directMark?: boolean;
  // 1 for the original attempt (or undefined on legacy attempts); 2+ for re-writes.
  // Ordered marker so the counting-attempt selection is stable when timestamps tie.
  attemptNumber?: number;
  // ISO timestamp of when the student STARTED the attempt. The availability gate
  // grandfathers on this (not submit time), so a student who legitimately started
  // in-window but submits slightly late keeps their work.
  startedAt?: string;
  completedAt: string;
  timeTaken?: number; // Seconds taken (total)
  // --- Soft-proctoring telemetry (recorded by PopQuizTaker, shown to teacher) ---
  timePerQuestion?: Record<string, number>; // questionId -> seconds spent
  timePerSection?: Record<string, number>;  // sectionId -> seconds spent
  focusLossCount?: number;    // times the student left the quiz tab/window
  focusLossSeconds?: number;  // total seconds spent away from the quiz
  pasteAttempts?: number;     // blocked paste attempts
  fullscreenExits?: number;   // times the student exited fullscreen
  fullscreenSecondsOutside?: number; // cumulative seconds spent outside fullscreen
  flagged?: boolean;          // any proctoring anomaly was recorded
  // Teacher-awarded marks for short-answer questions (questionId -> points).
  manualScores?: Record<string, number>;
  // Per-question written feedback (questionId -> text), AI-generated or teacher-edited.
  manualFeedback?: Record<string, string>;
}

// Teacher-attached file on a stream post (PDF, Office doc, etc.). Stored in
// Firebase Storage under stream-files/; url is the download-token URL.
export interface StreamAttachment {
  name: string;
  url: string;
  size: number;
  type: string;
  /** Firebase Storage object path (e.g. stream-files/{classId}/{uid}/...).
   *  Needed to delete or replace the underlying file later — the download URL
   *  alone can't address the object. Optional for older attachments that
   *  predate storage governance. */
  path?: string;
}

export interface StreamPost {
  id: string;
  title: string;
  content: string;
  type: string;
  isPinned?: boolean;
  isHighlighted?: boolean; // teacher-toggled visual emphasis (no notifications), shown to all
  isDraft?: boolean;      // staged/imported draft — hidden from students; teacher toggles visibility
  gcSourceId?: string;    // Google Classroom source item id — for re-import dedup
  originSourceId?: string; // Stable GC-origin id that PERSISTS through template creation/duplication (gcSourceId gets stripped to unlink). Used ALONGSIDE gcSourceId for dedup so re-importing an item never duplicates it, regardless of source.
  authorId?: string;
  authorName?: string;
  role?: 'teacher' | 'student';
  createdAt: string;
  timestamp?: string;
  link?: string;  // Single link (kept for backwards compatibility)
  resources?: ResourceLink[];  // Multiple resource links with titles
  /** Google Classroom add-on attachments (Practice Sets, interactive videos) that
   *  the API never sends us, inferred from the teacher's own instructions at import
   *  time. Drives the "needs rebuilding" checklist — see services/importGaps. */
  importGaps?: import('./services/importGaps').ImportGap[];

  imageUrls?: string[];  // Teacher-attached inline images (Firebase Storage download URLs)
  /** Firebase Storage object paths for the inline images, index-aligned with
   *  imageUrls. Needed to delete/reclaim the underlying image objects later (the
   *  download URL alone can't address the object). Optional for older images that
   *  predate storage governance. */
  imagePaths?: string[];
  attachments?: StreamAttachment[];  // Teacher-attached files (PDFs, Office docs, etc.)
  /**
   * A Google Drive template for this assignment. With perStudentCopy, each
   * student opens their OWN copy — made lazily the first time they open it and
   * owned by the teacher, exactly as Google Classroom does it. Reachable only
   * because the teacher picked the file, which is what grants this app
   * drive.file access to it.
   */
  driveTemplate?: { fileId: string; name: string; url: string; perStudentCopy: boolean };
  assessmentId?: string;
  dueDate?: string;  // ISO date string for assignments
  // If true, this assignment expects NO digital submission (in-class test, paper
  // handed in, etc.): the student submit UI is hidden and nobody is counted as
  // "not submitted." The teacher still grades it normally.
  noSubmissionRequired?: boolean;
  // Pop Quiz data
  popQuizId?: string; // If type is 'pop-quiz', link to PopQuiz
  // If true, this post created its linked assessment (for deletion - delete both together)
  createdAssessment?: boolean;
  // Conditional release — if set, students only see this post when condition is met
  releaseCondition?: ReleaseCondition;
  replies?: Array<{
    id: string;
    content: string;
    authorName: string;
    role: 'teacher' | 'student';
    createdAt: string;
  }>;
}

// Class Data
// A custom class reporting period for tracking progression over time.
// (Distinct from the school-admin `ReportingPeriod` above.)
export interface ClassReportingPeriod {
  id: string;
  name: string;       // "Beginning", "Midterm", "End", or anything custom
  createdAt?: string;
}

// Result of assessing one item: pass (knows it), almost (nearly / "almost
// there" — the optional yellow state), or fail (doesn't know it).
export type KinderResult = 'pass' | 'almost' | 'fail';

// One flashcard item in a kindergarten assessment (a letter, number, digraph,
// shape, or custom visual).
// An optional on-screen manipulative a teacher can display while administering a
// rubric concept (e.g. a timed subitizing flash, a set of counters, two piles).
export interface KinderDemo {
  kind: 'subitize' | 'counters' | 'twoPiles' | 'addRemove' | 'conservation' | 'abstraction' | 'compose';
  values?: number[]; // selectable/sequence counts (subitize flashes, compose targets, pick amounts)
  a?: number; b?: number; // twoPiles
  count?: number; // starting/base count
}

export interface KinderItem {
  id: string;
  displayValue: string; // "M", "ch", "8", a shape name, the dot count, or a concept name
  /**
   * 'emoji' is a COUNTING card drawn from meta.glyph and meta.count — eight
   * ducks — not eight ducks typed into displayValue. displayValue stays the
   * short label ("8 ducks") because it is what prints as a column header on
   * the class chart, heads the CSV column, and titles the grid cell; eight
   * emoji in any of those is unusable.
   */
  /**
   * 'oral_prompt' is a PHONOLOGICAL AWARENESS task: the teacher reads meta.prompt
   * aloud and listens for meta.expected. It has no child-facing card by design —
   * showing the text turns a listening task into a reading one — so these items
   * live only on a `kind: 'rubric'` assessment and never reach the slideshow.
   */
  itemType: 'letter_upper' | 'letter_lower' | 'letter_sound' | 'digraph' | 'number' | 'shape' | 'dots' | 'color' | 'concept' | 'emoji' | 'phrase' | 'oral_prompt' | 'custom';
  meta?: {
    color?: string; shapeName?: string; count?: number;
    /** The single picture repeated on an 'emoji' counting card. */
    glyph?: string;
    // Rubric/observational concepts (itemType 'concept') and oral tasks ('oral_prompt'):
    prompt?: string; expectation?: string;
    /**
     * What the teacher is listening for on an 'oral_prompt' — "5", "/t/", "hat".
     *
     * Separate from `expectation`, which names a CURRICULUM expectation ("15.4 —
     * stable order"). This is the answer to the question just asked.
     */
    expected?: string;
    lookForD?: string; lookForE?: string; lookForN?: string; nextSteps?: string;
    demo?: KinderDemo;
  };
}

/**
 * What kind of room a class is.
 *
 * 'esl' sits alongside the grade bands rather than inside one deliberately: an
 * ESL class is a teaching context, not an age, and it can hold anyone from a
 * six-year-old to a seventeen-year-old.
 */
export type ClassLevel = 'secondary' | 'elementary' | 'kindergarten' | 'esl';

/** Junior or Senior Kindergarten — the cohort split every kinder room has. */
export type KinderYear = 'JK' | 'SK';

/** A custom kindergarten grouping (JK/SK…). Defined in services/kinderLabels. */
export interface KinderLabel {
  id: string;
  name: string;
}

export interface KinderSection {
  id: string;
  name: string;         // "Uppercase Letters", "Digraphs", …
  items: KinderItem[];
  /**
   * Which heading this sits under, Literacy or Numeracy.
   *
   * Normally DERIVED from the item types, so every deck that already exists groups
   * correctly with no migration. Stored only when the contents cannot say: a
   * brand-new empty section (which would otherwise land under the wrong heading
   * and disappear from the teacher who just added it), or a mixed one a teacher
   * built deliberately. See services/kinderDomains.
   */
  domain?: import('./services/kinderDomains').KinderDomain;
}

// A kindergarten flashcard assessment. The teacher flips through items for one
// student, pressing pass/fail keys; results are recorded per reporting period so
// progression can be compared over time. No result cue shows on the slide.
export interface KinderAssessment {
  id: string;
  name: string;
  // 'flashcard' = the slideshow deck (letters/numbers/shapes…); 'rubric' = an
  // observational checklist scored D/E/N (e.g. Counting Concepts). Default flashcard.
  kind?: 'flashcard' | 'rubric';
  sections: KinderSection[];
  passKey: string;       // single key, lowercased (default 'q')
  failKey: string;       // single key, lowercased (default 'z')
  /**
   * The optional middle "almost there" (yellow) key.
   *
   * LEGACY, and a fallback only: keys now live on the teacher at
   * settings.kinderKeys, and these deck fields are read solely when a teacher
   * has never saved a global set. See services/kinderKeys.
   *
   * Unset does NOT make a deck binary — an earlier comment here said so and it
   * was wrong. Every reader treats 'almost' as first class regardless:
   * sectionSubtotal counts it, scoreOf scores it 0.5, the grid paints it
   * yellow, the printed chart prints a dash. What this gates is INPUT and a
   * few labels. Display of the yellow state follows recorded data instead —
   * gating it on this key was hiding marks children had earned on a printed
   * report.
   */
  almostKey?: string;
  // results[periodId][studentId][itemId] = 'pass' | 'almost' | 'fail' (= D/E/N for
  // rubric). Kept fully separate per item + per student + per period.
  results?: Record<string, Record<string, Record<string, KinderResult>>>;
  // Optional teacher observation notes, same keying: notes[periodId][studentId][itemId].
  notes?: Record<string, Record<string, Record<string, string>>>;
  createdAt?: string;
}

/** A teacher's pending request to join a class as a co-teacher via its join code.
 *  Stored on ClassData.coTeacherRequests until the owner approves or denies. */
export interface CoTeacherRequest {
  uid: string;
  email: string;
  name: string;
  requestedAt: string;
}

/**
 * Why a post's Google Classroom copy is behind, and what can be done about it.
 *
 *   'stale'   — the push failed; retrying the SAME fields could work.
 *   'blocked' — Google will never accept it. The fix is in Classroom.
 *
 * `fields` is what still needs to go over: a retry that re-sends a hardcoded
 * guess would "succeed" at something that never failed and clear the flag while
 * the real change is still missing.
 */
export interface ClassroomPostSyncState {
  state: 'stale' | 'blocked';
  reason?: 'attachments' | 'not_ours' | 'gone' | 'failed';
  /** What still needs to go over, so a retry sends THAT. */
  fields?: string[];
  /** Whose field names `fields` holds — post and assessment share spellings. */
  scope?: 'post' | 'assessment';
}

export interface ClassData {
  id: string;
  ownerId: string;
  name: string;
  code: string;
  joinCode?: string;
  semester: string;
  year?: string;
  academicYear?: string;
  markSetId: string;
  /**
   * Optional grade OUTPUT scale for this class (percent / letter / gpa / levels).
   * DISPLAY LAYER ONLY — the stored average stays a percent; this only changes how
   * that percent is rendered at student/parent/report-card display sites. Unset =
   * today's percentage behaviour. Seeded from the chosen mark set's defaultScale at
   * creation, overridable in the create-class wizard.
   */
  gradeScale?: GradeScale;
  /**
   * Credit value for this course (Ontario: 1.0 full, 0.5 half — e.g. Civics CHV2O).
   * Explicit, never derived: `code` is the course+SECTION string ("ADA3M102"), so the
   * trailing-5 half-credit convention can't be read off it without silently granting
   * the wrong credit. Unset = a full credit (see creditStatus.creditValueOf).
   * Only surfaced for schools with grantsOntarioCredits.
   */
  creditValue?: number;
  themeColor?: string;
  students: Student[];
  /** Students who LEFT the class (soft-remove). Kept out of `students` so all the
   *  active-roster/gradebook/attendance code ignores them, but preserved (with
   *  their id, so class.grades entries stay linked) for re-association when the
   *  SAME email/uid rejoins. A different new student never matches these. */
  formerStudents?: Student[];
  /**
   * Hydrated CLIENT-SIDE ONLY (never persisted on the class doc). Sensitive
   * student PII (parent contact, OEN, email, accommodations, notes) lives in the
   * owner/admin-only `classes/{id}/private/roster` doc, out of the classmate-
   * readable embedded students[] array. Read it via `studentPrivateOf()`.
   */
  studentPrivate?: Record<string, StudentPrivate>;
  assessments: Assessment[];
  grades: Record<string, Grade>;
  releasedGrades?: Record<string, Grade>;  // Grades visible to students (synced when teacher releases)
  gradesReleasedAt?: string;  // Timestamp of last grade release
  /**
   * Per-student KATC-weighted average, snapshotted at grade release. Student +
   * parent portals read this (they can't read the owner-only markSet) so the
   * number they see matches the teacher's report card. Keyed by studentId.
   */
  releasedAverages?: Record<string, number>;
  /**
   * The mark-set categories (axis definitions: id/name/color) snapshotted at grade
   * release, so the student portal can draw the SAME "Category breakdown" radar the
   * parent portal does WITHOUT reading the owner-only markSet. Mirrors
   * releasedAverages: written by releaseGrades from the class's effective markSet
   * (customMarkSet-resolved). Per-category VALUES are still computed client-side by
   * calculateCategoryAverage on releasedGrades — this only supplies the axes.
   */
  releasedCategories?: MarkSetCategory[];
  /**
   * Each assessment's weighting at the moment grades were released: assessmentId →
   * {categoryId, weight}. Only assessments that had a released grade.
   *
   * Compared against the live assessments to tell a teacher that the WEIGHTING has changed
   * since students last saw their marks — which `hasUnreleasedGrades` could not detect,
   * because it compares per-student values and the weighting is not one. See
   * services/releaseStaleness.
   */
  releasedWeighting?: import('./services/releaseStaleness').ReleasedWeighting;
  attendance: AttendanceDay[];
  stream?: StreamPost[];
  submissions?: Submission[];
  seatingChart?: SeatingChart;
  gcCourseId?: string;   // Linked Google Classroom course id (for grade push-back)
  /**
   * Per-post Google Classroom sync state, keyed by post id. Absent = in sync.
   *
   * Deliberately NOT a field on the post itself: flipping a flag inside the
   * stream array means rewriting the whole array, and students write replies
   * into that same array. A keyed map is a targeted field write that cannot
   * clobber anything.
   */
  gcPostSync?: Record<string, ClassroomPostSyncState>;
  /**
   * The teacher's own row order for the gradebook, as student ids. Absent means
   * alphabetical. Anyone missing from it sorts to the end, so a student added
   * after the arrangement was made is visible rather than lost.
   */
  studentOrder?: string[];
  gcCourseName?: string; // Display name of the linked course (for the UI)
  gcImportedFrom?: string; // Source GC course id this class was imported FROM (template imports are unlinked, so this is a non-linking marker used only to warn on re-import)
  isTemplate?: boolean;  // A reusable template class (unlinked from GC; duplicate it per section/semester)
  /** Provenance for a class built from a school's SHARED template library.
   *  Drives the "the template has been updated since you used it" marker — which
   *  is a marker only. The class is fully independent by design; nothing is ever
   *  pulled in automatically, because a teacher's edits must never be
   *  overwritten by an admin republishing. */
  sharedTemplateId?: string;
  sharedTemplateVersion?: number;
  /** Set when this template arrived from another teacher's share. Provenance only —
   *  the copy is owned outright by the recipient and is not linked to the original. */
  sharedFrom?: { uid: string; name: string; at: string; sourceClassId?: string };
  /**
   * Class kind. 'course' (default when unset — graded, credit-bearing, feeds the
   * SIS/timetable and the student directory) or 'group' (team, club, homeroom,
   * staff space — roster + stream only; NO grades, NO credit, NEVER synced to the
   * school student directory). The timetable only ever creates courses.
   */
  classType?: 'course' | 'group';
  /** For groups: the type label shown on the card's left spine — a built-in
   *  ('Team' | 'Club' | 'Homeroom' | 'Staff') or a custom label (≤12 chars). */
  groupType?: string;
  /** For groups: true when members are staff, not students. Staff-member groups are
   *  never synced to the student directory (a teacher must not become a directory
   *  student). Student membership is only allowed on groups, never on courses. */
  groupMembersAreStaff?: boolean;
  /** For groups: whether the card spine shows the type label (default) or the full
   *  group name. Name-on-spine truncates past ~14 chars; type stays legible. */
  spineLabelMode?: 'type' | 'name';
  coTeachers?: string[];      // uids of co-teachers with full access (owner adds/removes)
  coTeacherEmails?: string[]; // parallel display list (same order as coTeachers)
  /** Pending "join as co-teacher by code" requests, awaiting the owner's approve/deny.
   *  Written server-side (request-coteacher-join); cleared on approve/deny. */
  coTeacherRequests?: CoTeacherRequest[];
  // Panel/level of the class. Drives which tools appear (e.g. the kindergarten
  // flashcard assessment). Defaults to 'secondary' when unset.
  /**
   * What kind of room this is. Drives which teaching tools appear — see
   * services/classLevel.hasFlashcards, which is the ONE place that decides,
   * so adding a level here does not mean hunting for gates.
   */
  level?: ClassLevel;
  // Custom reporting periods for tracking progression over time (e.g. Beginning,
  // Midterm, End). Assessments record results per period.
  reportingPeriods?: ClassReportingPeriod[];
  /**
   * Work students have ATTACHED but not handed in — from Level Up or from
   * Google Classroom.
   *
   * Deliberately NOT in `submissions`: "has this student submitted?" is asked
   * in twenty-five files, and a draft in that array would count as a hand-in
   * everywhere one of them was missed. See services/workInProgress.
   */
  workInProgress?: import('./services/workInProgress').WorkInProgress[];
  // Kindergarten flashcard/slideshow assessments (letters, numbers, shapes…).
  kinderAssessments?: KinderAssessment[];
  /**
   * Up to four custom groupings for a kindergarten room (JK/SK and the like).
   * ORDERED: the order a teacher writes them in is the order the class lists
   * sort in, so JK-before-SK is expressed by writing JK first rather than by
   * hunting for a sort direction. Students point at these by id
   * (Student.kinderLabelId). See services/kinderLabels.
   */
  kinderLabels?: KinderLabel[];
  isArchived?: boolean;
  archivedAt?: string;
  autoDeleteDate?: string; // When archived class will be auto-deleted (based on plan tier)
  createdAt?: string;
  learningSkillCategories?: string[];
  learningSkillLevels?: { code: string; label: string; color: string }[]; // Customizable skill levels
  manualStudentMode?: boolean; // If true, disable student portal access
  /**
   * No marks in this class at all.
   *
   * For the rooms that are not about grades — kindergarten, ESL, a club, a
   * support block — where a gradebook grid is a filing cabinet nobody opens.
   * Attendance, the hub, seating, flashcards and gamification all still work;
   * the grade grid, learning skills, metrics, reports and assessments come off
   * the tab bar and off the routing, so no deep link or restored nav state can
   * land on them either.
   *
   * Reversible from Class Settings — nothing is deleted by turning it on.
   */
  gradesDisabled?: boolean;
  // Privacy settings for board compliance
  /**
   * PRIVACY FIRST — the strongest privacy posture a class can have.
   *
   * Implies `useNicknames` and, unlike it, is ONE-WAY: firestore.rules refuses to turn it off, so a
   * class cannot be quietly un-sealed to start collecting names on a roster students already share.
   *
   * What it means today: the pseudonym is `firstName`; a teacher-linked real name, guardian details
   * and OEN live in `classes/{id}/private/roster`, owner-only; and the AI Tutor and AI grading of
   * uploaded files are refused — see `privacyFirstRefusal`.
   *
   * Students join ANONYMOUSLY — a class code, a nickname they pick and a password the teacher can
   * reset. `join-class-email`, the already-signed-in join path, REFUSES a Privacy First class,
   * because in nickname mode it wrote the caller's real address onto the classmate-readable roster.
   *
   * WHAT IT DOES NOT YET MEAN, and do not claim otherwise until it is fixed: the real name a teacher
   * links is on OUR server, in the owner-only roster. The teacher-held vault that removes it
   * entirely is a later stage. So this is "no student identity on the document classmates can read,
   * and none collected from the student at all" — not "no PII on the server".
   *
   * Grandfathered nickname classes (`useNicknames` without this flag) DO still store a
   * Google-signed-in student's address on the roster. They never claimed otherwise; the dual-key
   * design that would fix them is tracked separately.
   */
  privacyFirst?: boolean;
  useNicknames?: boolean; // If true, students use nicknames instead of real names (no PII collected)
  disableStudentHub?: boolean; // If true, student portal is completely disabled for this class
  usedNicknames?: string[]; // Track which nicknames have been claimed in this class
  showRealNames?: boolean; // If true, printouts/leaderboards show real names instead of nicknames
  // Gamification
  gamificationSettings?: GamificationSettings;
  xpEvents?: XPEvent[];
  // Report mode for report card generation
  reportMode?: 'secondary' | 'elementary';
  // Set when a personal (non-school) teacher locks this class's report-card
  // comments. School classes lock via the sign-off approval instead.
  reportCommentsLockedAt?: string;
  // Set server-side when a school admin signs off this class's report-card
  // comments. The approved text is written onto each student.reportComment and
  // this stamp locks the class as the canonical, final version.
  reportCommentsFinalizedAt?: string;
  // Class-wide submission toggle - if false, students cannot submit ANY assignments
  acceptingSubmissions?: boolean;
  // Opt-in: email students the evening before an assignment is due, if they
  // haven't submitted. Only assignments with a valid due date. Default off —
  // a scheduled function skips any class where this isn't explicitly true.
  dueReminders?: boolean;
  // Grade history - audit trail for all grade changes
  gradeHistory?: GradeHistoryEntry[];
  // Saved rubrics for this class (can be attached to assessments)
  rubrics?: Rubric[];
  // Pop Quizzes for this class
  popQuizzes?: PopQuiz[];
  popQuizAttempts?: PopQuizAttempt[];
  // Default gradebook mode when opening this class (defaults to 'grades' if not set)
  /**
   * This class's own answer for specific gradebook tabs.
   *
   * SPARSE: it names only the tabs this class decides for itself, and every other
   * tab keeps following the teacher's `settings.hiddenGradebookModes` /
   * `gradebookTabLabels`, including later changes to them. Copying the whole
   * global set onto the class would freeze it at today's preferences and stop
   * tracking the default the teacher thinks they are editing.
   * See services/navPreferences.
   */
  tabOverrides?: import('./services/navPreferences').ClassTabOverrides;
  defaultGradebookMode?: 'grades' | 'skills' | 'reports' | 'metrics' | 'assessments' | 'attendance' | 'seating' | 'stream' | 'gamification' | 'pathways' | 'curriculum';
  // Custom markset for this class (local copy that doesn't affect master markset)
  customMarkSet?: {
    name?: string;  // Optional custom name
    categories: MarkSetCategory[];  // Local category weights
  };
  /**
   * A COPY of the owner's mark set, stored on the class so a CO-TEACHER can resolve
   * it. markSets are top-level docs readable only by their owner (firestore.rules),
   * and a class references one by id — so a co-teacher's lookup always missed and
   * silently fell back to their OWN first mark set. That didn't just hide the
   * owner's categories: calculateAverage walks markSet.categories, so any category
   * missing from the fallback was dropped from the weighting and the two teachers
   * saw different final marks for the same student.
   *
   * Owner behaviour is unchanged (they still resolve the live doc by id); this is
   * only the fallback the co-teacher lands on. Refreshed whenever the owner edits
   * that mark set, so the two can't drift into disagreeing again.
   */
  sharedMarkSet?: {
    id: string;            // the source markSets doc id, so staleness is detectable
    name?: string;
    categories: MarkSetCategory[];
    updatedAt: string;
  };
  // Learning Pathways
  learningPathways?: LearningPathway[];
  pathwayProgress?: Record<string, PathwayProgress>; // key: `${studentId}-${pathwayId}`
  // Curriculum alignment
  curriculumLink?: CurriculumLink;
  curriculumLink2?: CurriculumLink; // Second curriculum for split/combined classes
  // School linkage — set when class is created in a school context
  // Personal workspace classes have no schoolId (null/undefined)
  schoolId?: string;
  /**
   * Tier 4.6 pre-staged class claim. Set when a timetable publishes
   * classes for a CSV-imported teacher who hasn't actually signed up
   * yet — `ownerId` is a stub uid (e.g. `stub_jane_doe_x9b3a4`), and
   * `claimedByEmail` is the email lower-cased. On the teacher's first
   * sign-in with that verified email, claim-teacher-classes flips
   * `ownerId = auth.uid` and clears this field.
   *
   * Firestore rules also allow read against this field (`auth.token.email`
   * + `email_verified`) so the teacher can see the class before claim.
   */
  claimedByEmail?: string;
  /**
   * Tier 4.6 pre-staged class active-from date. Optional gating —
   * grading UI shows a "pre-staged, opens {activeAt}" banner before
   * this date. Roster/lesson plans still work; only grade entry is
   * gated. Stored as ISO date.
   */
  activeAt?: string;
  /** Lightweight teacher-managed schedule slot for this class — independent
   *  of the school-side master timetable. Surfaced on sub plans and student
   *  schedules. Set in ClassSettingsModal. */
  scheduleSlot?: {
    room?: string;
    startTime?: string;     // "HH:MM" (24h)
    endTime?: string;
    days?: string[];        // ['Mon','Tue','Wed','Thu','Fri']
  };
  /** Free-text "what the sub should know" notes for this class.
   *  Includes today's plan, classroom routines, where supplies are, etc.
   *  Snapshotted into SubPackageClassSnapshot.classroomNotes when a sub
   *  package is generated. Teacher-edited via class settings. */
  subInstructions?: string;
  /** Free-text title for today's lesson (used in sub package). */
  subLessonTitle?: string;
  /** Free-text body for today's lesson. Will be replaced by the proper
   *  lesson planner in Phase 7. */
  subLessonBody?: string;
  /** Flat array of Firebase auth uids that are members of this class.
   *  Used by Firestore rules to gate read access — replaces the previous
   *  `allow read: if isAuthenticated()` rule that let any auth user
   *  getDoc any class. Maintained by:
   *    - dataService.addStudent (when student has linked auth uid)
   *    - join-class-nickname / login-class-nickname netlify functions
   *    - claim-class-membership netlify function (email-authed students on first sign-in)
   *    - syncSchoolStudentToClass
   *  Always includes the class ownerId. Backfilled via migrate-class-members. */
  classMembers?: string[];
  /**
   * uid → studentId, for students signed in with an account rather than a nickname.
   *
   * ── WHY THIS HAD TO EXIST ───────────────────────────────────────────────────────────────────
   *
   * The student portal found "which of these thirty rows is me?" by comparing `student.email` to the
   * signed-in user's email. In a Privacy First class the email is encrypted and the class-document
   * copy is blank by design — so converting a live class to Privacy First made every account-joined
   * student unresolvable: no grades, no attendance, and every action handler returning early on
   * `!student`. They could not recover either, because `join-class-email` refuses a Privacy First
   * class and a nickname join would create a second, empty row while their marks sat on the first.
   *
   * `classMembers` holds their uids but says nothing about which row is whose, and `authUids` — which
   * does — lives in the owner-only private roster that students cannot read. So the mapping is kept
   * here, on the document a student can already read.
   *
   * It is not identifying: a classmate can already see every uid in `classMembers` and every
   * placeholder in `students`, and a uid is opaque. Resolution goes through
   * `selfStudentOf`, never a raw lookup.
   */
  memberStudentIds?: Record<string, string>;
  /**
   * Teachers who used to own this class and keep a tail of read access after a handover.
   *
   * `transferClass` deliberately leaves the outgoing owner in `classMembers` so they can finish
   * exporting, and nothing removes them. Once the vault began reconciling `classMembers` against the
   * roster, that made them a uid with access and no student row — reported as a person who "cannot
   * see their marks", forever, on every transferred class.
   */
  formerOwners?: string[];
  /**
   * The catalog courses this class runs — one id, or two when it is a COMBINED
   * (stacked) section such as ENG3U + EWC4U taught together.
   *
   * Present only on school classes published from a timetable. It is what lets
   * an admin change a student's course code by picking from the codes this
   * class legitimately offers, rather than typing one — a typo here becomes a
   * credit that cannot be transcripted.
   */
  linkedCourseIds?: string[];
  /** The codes those ids resolve to, same order. Denormalised so the roster can
   *  be rendered without reading the course catalog. */
  linkedCourseCodes?: string[];
}

// ─── Curriculum Alignment ─────────────────────────────────────────────────────

// Describes which Ontario curriculum doc + course a class is aligned to
export interface CurriculumLink {
  filename: string;       // e.g. "ON-science-11-12-2008.json"
  subject: string;        // e.g. "Science"
  courseCode: string;     // e.g. "SBI3U"
  courseTitle: string;    // e.g. "Biology, Grade 11, University Preparation"
  showExpectationsToStudents?: boolean;
}

// A single curriculum expectation attached to an assessment
export interface CurriculumExpectation {
  code: string;           // e.g. "B1"
  /**
   * The MINISTRY's wording. Crown copyright (© King's Printer for Ontario).
   *
   * Optional now, and omitted on new writes whenever `goal` exists — see
   * `expectationForStorage` in services/curriculumGoals. Ontario permits free reproduction
   * NON-COMMERCIALLY with credit, and this is a commercial product, so the long-term aim is that
   * no new record carries it. Records written before that change still do.
   */
  text?: string;
  /**
   * OUR wording: a plain-language statement of what to look for, keyed to the same code.
   *
   * Not a paraphrase of `text` — a rewording that tracks the original sentence by sentence is a
   * derivative work. Preferred over `text` by every surface, via `displayExpectation`.
   */
  goal?: string;
  strandCode: string;     // e.g. "B"
  strandTitle: string;    // e.g. "Diversity of Living Things"
}

// A reusable assessment template saved from the Assessment Lab
// Stored in the `assessmentTemplates` Firestore collection (separate from class data)
/**
 * A class template published into a school's SHARED library.
 *
 * The point of the feature: an enterprise sets its courses up centrally, and any
 * teacher in the school builds their own class from one. What a teacher gets is a
 * COPY they own outright — later changes to the template never reach it. There is
 * deliberately no live link, only a marker saying the source has moved on.
 *
 * SNAPSHOT, NOT A POINTER. The publishable payload is copied into this document
 * rather than referenced, for two reasons: a teacher cannot read another
 * teacher's class doc, and a pointer to a live class is precisely what let a
 * roster leak through the template system before (see
 * netlify/functions/clean-template-contamination.ts). The snapshot is built by
 * buildClassCopy(), the same function the Duplicate and Share paths use, so the
 * "never carry roster / grades / Classroom links / uploaded files" rules cannot
 * apply to one path and silently miss this one. firestore.rules independently
 * REFUSES a write whose snapshot carries students, grades, submissions or
 * attendance — belt and braces, because this document is readable by every
 * teacher in the school.
 */
export interface SharedClassTemplate {
  id: string;
  schoolId: string;
  /** Shown in the library. Defaults to the source template's name. */
  name: string;
  code?: string;
  /** Optional blurb from the publisher: who it's for, what's in it. */
  description?: string;
  /** The sanitized class payload a teacher's copy is built from. */
  snapshot: Partial<ClassData>;
  publishedBy: string;
  publishedByName?: string;
  publishedAt: string;
  updatedAt: string;
  /**
   * Bumped on every republish.
   *
   * This is what the "updated since you used it" marker compares against the
   * version stamped on a teacher's class. A timestamp would work too, but a
   * counter makes "you are 2 versions behind" expressible and can't be confused
   * by clock skew between the publisher's browser and the teacher's.
   */
  version: number;
  /** The publisher's own template this was taken from. Informational only — it is
   *  never read back, so an admin deleting their copy cannot break the library. */
  sourceClassId?: string;
}

export interface AssessmentTemplate {
  id: string;
  ownerId: string;
  classId?: string;       // If set, affiliated with a specific class
  className?: string;     // Snapshot of class name at save time
  title: string;
  categoryId: string;
  outOf: number;
  weight?: number;
  type?: 'Diagnostic' | 'Formative' | 'Summative' | 'Portfolio';
  description?: string;
  rubric?: Rubric;
  curriculumExpectations?: CurriculumExpectation[];
  curriculumCourseCode?: string;
  curriculumCourseTitle?: string;
  /**
   * Which curriculum document those expectations came from.
   *
   * Needed to print the right copyright notice when the template is exported: the notice differs
   * by jurisdiction, and for CCSS the licence prescribes its exact wording. Absent on templates
   * saved before this field existed, which fall back to the generic notice.
   */
  curriculumFilename?: string;
  savedFromPublicId?: string;   // Set if saved from community; prevents re-publishing
  /** The generated content itself, so a saved item can be recalled into the Lab
   *  and reused in any class: markdown for assignment/lesson/rubric, or the quiz
   *  JSON string for popquiz. contentKind tells the Lab how to restore it. */
  content?: string;
  contentKind?: 'assignment' | 'lesson' | 'rubric' | 'popquiz';
  createdAt: string;
  updatedAt?: string;
}

// ─── Learning Pathways ────────────────────────────────────────────────────────

export type PathwayNodeType = 'assignment' | 'resource' | 'lesson' | 'checkpoint';

export interface PathwayNode {
  id: string;
  order: number;
  type: PathwayNodeType;
  title: string;
  description?: string;
  contentUrl?: string;      // For 'resource' nodes
  lessonContent?: string;   // For 'lesson' nodes (plain text/markdown)
  assessmentId?: string;    // For 'assignment' nodes — links to an existing Assessment
  xpValue: number;
  required: boolean;        // Must complete to unlock next node (linear sequence)
  estimatedMinutes?: number;
}

export interface LearningPathway {
  id: string;
  title: string;
  description?: string;
  classId: string;
  createdAt: string;
  status: 'draft' | 'published';
  nodes: PathwayNode[];
  xpBonus: number;          // Bonus XP for completing the entire pathway
}

export interface PathwayProgress {
  pathwayId: string;
  studentId: string;
  completedNodeIds: string[]; // IDs of manually-completed nodes (resource/lesson/checkpoint)
  startedAt: string;
  completedAt?: string;       // Set when all required nodes are done
}

// A community-shared assessment template
// Stored in the `publicTemplates` Firestore collection
export interface PublicTemplate {
  id: string;
  ownerId: string;
  ownerName: string;
  ownerTitle?: string;
  ownerSchool?: string;
  title: string;
  type?: 'Diagnostic' | 'Formative' | 'Summative' | 'Portfolio';
  subject?: string;
  gradeLevel?: string;
  courseCode?: string;
  content: string;           // Serialized rubric / assignment content (markdown or JSON)
  description?: string;
  categoryId: string;
  outOf: number;
  weight?: number;
  rubric?: Rubric;
  curriculumExpectations?: CurriculumExpectation[];
  /**
   * Which curriculum document those expectations came from, carried across the share so the
   * recipient's copy can print the right copyright notice. The notice differs by jurisdiction and
   * CCSS prescribes its exact wording, so losing this on the way through would make a shared
   * template's export credit the wrong authority.
   */
  curriculumFilename?: string;
  upvotes: number;
  upvotedBy: string[];       // UIDs who upvoted; used to toggle
  saves: number;             // How many times saved to a personal library
  views: number;             // Incremented when someone previews/expands
  // Rating: one rating per user, 1–5 stars
  ratingSum: number;         // Sum of all star ratings
  ratingCount: number;       // Number of users who rated
  ratedBy: Record<string, number>; // uid → star value (1–5)
  publishedAt: string;
  tags?: string[];
  /** Required at publish: the specific topic/skill (e.g. "Quadratic factoring"). */
  topic?: string;
  /** The generated body's kind, so a saved copy restores it correctly in the Lab. */
  contentKind?: 'assignment' | 'lesson' | 'rubric' | 'popquiz';
  // Denormalized for filtering/sorting
  contentType?: 'assignment' | 'rubric' | 'lesson' | 'popquiz';
}

// A reusable pathway template saved from PathwaysView
// Stored in the `pathwayTemplates` Firestore collection (separate from class data)
export interface PathwayTemplate {
  id: string;
  ownerId: string;
  sourceClassId?: string;   // Class the pathway was saved from
  sourceClassName?: string; // Snapshot of class name at save time
  title: string;
  description?: string;
  nodes: PathwayNode[];     // assessmentId is stripped (class-specific)
  xpBonus: number;
  nodeCount: number;        // denormalized for quick display
  savedAt: string;
}

// 50 pre-defined nicknames for privacy mode
export const STUDENT_NICKNAMES = [
  'Astro', 'Blaze', 'Comet', 'Dash', 'Echo',
  'Falcon', 'Galaxy', 'Hawk', 'Ivy', 'Jet',
  'Karma', 'Luna', 'Mars', 'Nova', 'Orion',
  'Phoenix', 'Quest', 'Raven', 'Storm', 'Titan',
  'Uno', 'Viper', 'Wolf', 'Xenon', 'Yeti',
  'Zenith', 'Ace', 'Bolt', 'Cipher', 'Dune',
  'Ember', 'Frost', 'Griffin', 'Halo', 'Ion',
  'Jade', 'Knight', 'Lynx', 'Mystic', 'Neon',
  'Omega', 'Pixel', 'Quasar', 'Rocket', 'Sage',
  'Thunder', 'Ultra', 'Vertex', 'Whisper', 'Zephyr'
];

// Deletion Record
// ─── Sub Plan Mode (Phase 1) ─────────────────────────────────────────────────

/**
 * Per-class snapshot baked into a sub package. Lets the public sub-access page
 * render without needing live class reads (and without exposing fields a sub
 * doesn't need — full IEPs, parent contact, grades, etc.).
 */
export interface SubPackageClassSnapshot {
  classId: string;
  className: string;
  classCode: string;
  themeColor?: string;
  /** Schedule info snapshot — room, time, meeting days. Surfaced near the top
   *  of each section on the sub plan so the sub knows where + when each class is. */
  room?: string;
  startTime?: string;
  endTime?: string;
  days?: string[];
  /** Today's lesson if one exists. Pulled from classes/{id}/lessons in Phase 7;
   *  for the MVP we use the class's `subInstructions` free-text field. */
  todaysLessonTitle?: string;
  todaysLessonBody?: string;
  /** Free-text "what the sub should know" notes the teacher writes per class. */
  classroomNotes?: string;
  /** Roster trimmed to fields a sub actually needs. NO email, NO parent info,
   *  NO grades, NO OEN. Optional accommodations note is included verbatim
   *  so teachers can write things like "extended time" or "separate setting". */
  roster: Array<{
    studentId: string;
    firstName: string;
    lastName: string;
    studentNumber?: string;
    photoUrl?: string;
    accommodationsNote?: string;
  }>;
  /** Optional seating layout snapshot if the class has one configured. */
  seatingChart?: SeatingChart;
}

export interface SubPackage {
  /** Random 64-char token. Doubles as Firestore doc ID so the public access
   *  endpoint can do a single-doc get without a list query. */
  id: string;
  teacherUid: string;
  teacherName: string;
  /** ISO date string (YYYY-MM-DD) — the day the sub is filling in. */
  date: string;
  classSnapshots: SubPackageClassSnapshot[];
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'revoked';
  /** Set when the sub first opens the package. Useful for audit/debug. */
  firstAccessedAt?: string;
  accessCount?: number;
}

export interface DeletionRecord {
  id: string;
  ownerId: string; // Teacher ID for Firestore queries
  type: 'class' | 'student' | 'assessment' | 'stream-post';
  name: string;
  timestamp: string;
  data: any;
  context?: {
    classId?: string;
    className?: string;
  };
  // For student/assessment deletions: the grades purged with the item, snapshotted
  // (keyed studentId-assessmentId) so a restore can re-attach them. Absent on class
  // deletions (the whole grades map rides along in `data`) and on legacy records.
  gradesSnapshot?: Record<string, Grade>;
  releasedGradesSnapshot?: Record<string, Grade>;
  /** Metadata only. Observation evidence is permanently erased, never snapshotted.
   * Pending prevents restore/clear until removal and server cleanup have completed. */
  observationErasure?: 'pending' | 'complete';
  /** Zero-observation admin removal uses a capture barrier, never admin erasure. */
  observationDeletionRoute?: 'legacy-admin';
  // For STUDENT deletions: the rest of the student's data that delete also purges,
  // snapshotted for existing undo behavior; kindergarten observations are excluded.
  // (Portal access — classMembers/authUids — is
  // deliberately NOT restored; the student rejoins to regain it.) Absent on legacy
  // records → those restore roster + grades only, as before.
  studentResidue?: {
    submissions?: Submission[];
    attendance?: Array<{ date: string; record?: 'P' | 'A' | 'L' | 'E'; lateTime?: string; note?: string }>;
    xpEvents?: XPEvent[];
    seat?: { position?: { row: number; col: number } | null; freeform?: { x: number; y: number }; seat?: string | null };
    studentPrivate?: Record<string, unknown>;
  };
  /**
   * True when the deleted class promised it holds no personal information (Privacy First or nickname
   * mode), so this snapshot was stripped of owner-only PII before it was stored.
   *
   * Recorded rather than recomputed, because the class is gone by the time anything reads this.
   * `addToDeletionHistory` uses it to REFUSE a snapshot that still carries PII, so a delete path written
   * later cannot bypass the strip by simply not calling it.
   */
  privacyFirstSnapshot?: boolean;
}

/**
 * Tier 5.2 — generic approval queue.
 *
 * Three first-day uses: graduation (board-admin signs off), final
 * grade sign-off (school admin), and report card comment sign-off
 * (school admin). Per-type payload is loosely typed because each
 * approval form needs different fields — readers narrow by `type`.
 */
export type PendingApprovalType = 'graduation' | 'final_grade' | 'report_card_comment' | 'year_rollover';
export type PendingApprovalStatus = 'pending' | 'approved' | 'rejected';
export type PendingApprovalApprover = 'school_admin' | 'board_admin';

export interface PendingApproval {
  id: string;
  schoolId: string;
  type: PendingApprovalType;
  /** Who needs to sign off. School admins see school-scoped queues;
   *  board admins see graduations + anything escalated. */
  approver: PendingApprovalApprover;
  submittedBy: string;
  submittedByName?: string;
  submittedAt: string;
  status: PendingApprovalStatus;
  /** Type-specific data — narrowed at read time. */
  payload: Record<string, any>;
  /** Display label for queue rows ("Grade 12 — John Smith", "SBI3U Final Grades"). */
  subject: string;
  /** Optional secondary line for the queue ("Class of 2026", "Term 1"). */
  context?: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  /** Required if status='rejected'; optional otherwise. */
  reviewNote?: string;
}

// View State
export type ViewState =
  | 'dashboard'
  | 'gradebook'
  | 'students'
  | 'student-detail'
  | 'assessment-detail'
  | 'mark-sets'
  | 'assessment-lab'
  | 'ai-tools'
  | 'community'
  | 'walkthroughs'
  | 'tutorials'
  | 'settings'
  | 'admin'
  | 'school-timetable';

// Theme
export interface Theme {
  id: string;
  label: string;
  sidebarStyle: React.CSSProperties;
  mainStyle: React.CSSProperties;
  requiresPro?: boolean;
  textColor?: 'light' | 'dark';
  primaryColor?: string; // Primary accent color for UI elements
}

// Gamification Types

// Badge Rarity determines visual styling and prestige
export type BadgeRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'secret';

// Badge Definition - static configuration for each badge type
export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  hint?: string; // Hint shown for locked badges (undefined = fully hidden for secret badges)
  image: string; // Path to badge image in /badges/ folder
  rarity: BadgeRarity;
  xpReward: number;
  isSecret?: boolean; // Hidden until earned
  // Category for organizing in the library
  category: 'getting_started' | 'engagement' | 'achievement' | 'dedication' | 'social' | 'special';
  // If true, badge can be earned multiple times (once per assessment)
  // Examples: on_time, early_bird, ace - earned each time the condition is met
  isRepeatable?: boolean;
}

// Earned Badge - stored on the student record
export interface EarnedBadge {
  badgeId: string;
  earnedAt: string; // ISO timestamp
  classId?: string; // Which class it was earned in (if applicable)
  assessmentId?: string; // Which assessment it was earned on (for repeatable badges)
}

// Daily Games tracking data
// Daily-cadence games (once per calendar day) and weekly-cadence specials
// (once per ISO week). Weekly games are gated by lastPlayedWeek, not lastPlayed.
export type DailyGameType =
  | 'mathFlash'
  | 'numberSequence'
  | 'wordScramble'
  | 'wordle'
  | 'patternRecall'
  | 'boggle'
  | 'bossWordle'
  | 'weeklyGauntlet';

// Canonical game lists. DAILY games record their play date in dailyGames.lastPlayed
// (one per day); WEEKLY games record an ISO week key in dailyGames.lastPlayedWeek.
// Derive counts/denominators from these so adding a game updates every surface —
// never hardcode "of 4".
export const WEEKLY_GAME_TYPES: DailyGameType[] = ['bossWordle', 'weeklyGauntlet'];
export const DAILY_GAME_TYPES: DailyGameType[] = [
  'mathFlash', 'numberSequence', 'wordScramble', 'wordle', 'patternRecall', 'boggle',
];

export interface DailyGameResult {
  date: string; // ISO date (YYYY-MM-DD)
  correct: boolean;
  xpEarned: number;
  score?: number; // Number of questions correct (for multi-question games)
}

export interface DailyGamesData {
  // Last played date for each game type (YYYY-MM-DD format)
  lastPlayed: Record<DailyGameType, string>;
  // Last played ISO-week key for weekly-cadence games (gameType -> "2026-W30")
  lastPlayedWeek?: Record<string, string>;
  // Consecutive-week streak for the Weekly Gauntlet
  weeklyStreak?: number;
  // History of results (optional, for streaks tracking)
  history?: DailyGameResult[];
  // Total XP earned from daily games
  totalXpFromGames?: number;
}

// XP History entry for tracking XP changes
export interface XPHistoryEntry {
  timestamp: string;  // ISO date string
  amount: number;     // XP change (+5, -10, etc.)
  reason: string;     // Human-readable reason ("Attendance - Present", "Badge: On Time")
  type: 'attendance' | 'badge' | 'submission' | 'manual' | 'other';
  classId?: string;   // Which class this was for
}

// Student badge tracking data
export interface StudentBadgeData {
  earnedBadges: EarnedBadge[];
  // Tracking counters for badge progress
  totalLogins: number;
  loginStreak: number;
  maxLoginStreak: number;
  lastLoginDate?: string;
  totalSubmissions: number;
  totalQuestionsAsked: number;
  totalReplies: number;
  // Attendance XP tracking
  attendanceXP?: number;  // Cumulative XP from attendance (capped at 0 minimum)
  // Track processed attendance to handle edits (classId-date -> status processed)
  processedAttendance?: Record<string, string>;
  // XP history - last 15 entries, max 30 days old
  xpHistory?: XPHistoryEntry[];
}

// Badge definitions - the master list of all badges
export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  // === GETTING STARTED (Common) ===
  {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Joined your first class',
    hint: 'Join a class to unlock',
    image: '/badges/first_steps.png',
    rarity: 'common',
    xpReward: 25,
    category: 'getting_started'
  },
  {
    id: 'new_kid',
    name: 'New Kid',
    description: 'Logged in for the first time',
    hint: 'Log in to get started',
    image: '/badges/new_kid.png',
    rarity: 'common',
    xpReward: 10,
    category: 'getting_started'
  },
  {
    id: 'first_submit',
    name: 'Hand It In',
    description: 'Submitted your first assignment',
    hint: 'Submit an assignment',
    image: '/badges/first_submit.png',
    rarity: 'common',
    xpReward: 30,
    category: 'getting_started'
  },

  // === ENGAGEMENT (Common/Uncommon) ===
  {
    id: 'curious_mind',
    name: 'Curious Mind',
    description: 'Asked your first question',
    hint: 'Ask a question in the class hub',
    image: '/badges/curious_mind.png',
    rarity: 'common',
    xpReward: 20,
    category: 'social'
  },
  {
    id: 'conversation_starter',
    name: 'Conversation Starter',
    description: 'Replied to a post',
    hint: 'Reply to an announcement or question',
    image: '/badges/conversation_starter.png',
    rarity: 'common',
    xpReward: 20,
    category: 'social'
  },
  {
    id: 'on_time',
    name: 'On Time',
    description: 'Submitted before the deadline',
    hint: 'Submit an assignment before it\'s due',
    image: '/badges/on_time.png',
    rarity: 'common',
    xpReward: 25,
    category: 'achievement',
    isRepeatable: true  // Earn on every on-time submission
  },
  {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Submitted 24+ hours before deadline',
    hint: 'Submit super early!',
    image: '/badges/early_bird.png',
    rarity: 'uncommon',
    xpReward: 50,
    category: 'achievement',
    isRepeatable: true  // Earn on every early submission
  },

  // === DEDICATION (Uncommon/Rare) ===
  {
    id: 'regular',
    name: 'Regular',
    description: 'Logged in 10 times',
    hint: 'Keep coming back!',
    image: '/badges/regular.png',
    rarity: 'uncommon',
    xpReward: 40,
    category: 'dedication'
  },
  {
    id: 'bookworm',
    name: 'Bookworm',
    description: 'Submitted 10 assignments',
    hint: 'Submit 10 assignments',
    image: '/badges/bookworm.png',
    rarity: 'uncommon',
    xpReward: 75,
    category: 'achievement'
  },
  {
    id: 'dedicated',
    name: 'Dedicated',
    description: 'Logged in 50 times',
    hint: 'Keep showing up!',
    image: '/badges/dedicated.png',
    rarity: 'rare',
    xpReward: 100,
    category: 'dedication'
  },
  {
    id: 'veteran',
    name: 'Veteran',
    description: 'Logged in 100 times',
    hint: 'A true regular',
    image: '/badges/veteran.png',
    rarity: 'rare',
    xpReward: 200,
    category: 'dedication'
  },
  {
    id: 'legend',
    name: 'Legend',
    description: 'Logged in 500 times',
    hint: 'Legendary dedication',
    image: '/badges/legend.png',
    rarity: 'legendary',
    xpReward: 500,
    category: 'dedication'
  },

  // === ACHIEVEMENT (Uncommon/Rare/Epic) ===
  {
    id: 'rising_star',
    name: 'Rising Star',
    description: 'Reached Level 5',
    hint: 'Level up to 5',
    image: '/badges/rising_star.png',
    rarity: 'uncommon',
    xpReward: 50,
    category: 'achievement'
  },
  {
    id: 'scholar',
    name: 'Scholar',
    description: 'Reached Level 10',
    hint: 'Reach the double digits',
    image: '/badges/scholar.png',
    rarity: 'rare',
    xpReward: 150,
    category: 'achievement'
  },
  {
    id: 'ace',
    name: 'Ace',
    description: 'Scored 100% on an assessment',
    hint: 'Perfect score!',
    image: '/badges/ace.png',
    rarity: 'rare',
    xpReward: 100,
    category: 'achievement',
    isRepeatable: true  // Earn on every perfect score
  },
  {
    id: 'improver',
    name: 'Improver',
    description: 'Improved your grade by 10%+ between terms',
    hint: 'Show growth over time',
    image: '/badges/improver.png',
    rarity: 'uncommon',
    xpReward: 75,
    category: 'achievement'
  },
  {
    id: 'perfectionist',
    name: 'Perfectionist',
    description: 'Maintained 90%+ average for a semester',
    hint: 'Excellence sustained',
    image: '/badges/perfectionist.png',
    rarity: 'epic',
    xpReward: 300,
    category: 'achievement'
  },

  // === STREAKS (Rare/Epic) ===
  {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: '7 day login streak',
    hint: 'Log in every day for a week',
    image: '/badges/week_warrior.png',
    rarity: 'uncommon',
    xpReward: 60,
    category: 'dedication'
  },
  {
    id: 'streak_master',
    name: 'Streak Master',
    description: '30 day login streak',
    hint: 'A full month of dedication',
    image: '/badges/streak_master.png',
    rarity: 'rare',
    xpReward: 200,
    category: 'dedication'
  },
  {
    id: 'marathon_runner',
    name: 'Marathon Runner',
    description: '100 day login streak',
    hint: 'Unstoppable!',
    image: '/badges/marathon_runner.png',
    rarity: 'epic',
    xpReward: 500,
    category: 'dedication'
  },

  // === SOCIAL (Uncommon/Rare) ===
  {
    id: 'helpful',
    name: 'Helpful',
    description: 'Replied to 10 posts',
    hint: 'Help your classmates',
    image: '/badges/helpful.png',
    rarity: 'uncommon',
    xpReward: 50,
    category: 'social'
  },
  {
    id: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Replied to 50 posts',
    hint: 'The life of the class',
    image: '/badges/social_butterfly.png',
    rarity: 'rare',
    xpReward: 150,
    category: 'social'
  },

  // === SECRET BADGES ===
  {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Submitted between midnight and 4am',
    image: '/badges/night_owl.png',
    rarity: 'secret',
    xpReward: 50,
    isSecret: true,
    category: 'special',
    isRepeatable: true  // Earn on every late-night submission
  },
  {
    id: 'overachiever',
    name: 'Overachiever',
    description: 'Scored above 100% (bonus marks!)',
    image: '/badges/overachiever.png',
    rarity: 'secret',
    xpReward: 75,
    isSecret: true,
    category: 'special',
    isRepeatable: true  // Earn on every overachieving score
  },
  {
    id: 'lightning',
    name: 'Lightning',
    description: 'Submitted within 5 minutes of assignment posting',
    image: '/badges/lightning.png',
    rarity: 'secret',
    xpReward: 40,
    isSecret: true,
    category: 'special',
    isRepeatable: true  // Earn on every lightning-fast submission
  },
  {
    id: 'procrastinator',
    name: 'Procrastinator',
    description: 'Submitted in the last 5 minutes before deadline',
    image: '/badges/procrastinator.png',
    rarity: 'secret',
    xpReward: 25,
    isSecret: true,
    category: 'special',
    isRepeatable: true  // Earn on every last-minute submission
  },
  {
    id: 'perfect_attendance',
    name: 'Perfect Attendance',
    description: 'Present every day for an entire month',
    hint: 'Never miss a day',
    image: '/badges/perfect_attendance.png',
    rarity: 'rare',
    xpReward: 150,
    category: 'dedication'
  }
];

// Helper to get badge by ID
export const getBadgeDefinition = (badgeId: string): BadgeDefinition | undefined => {
  return BADGE_DEFINITIONS.find(b => b.id === badgeId);
};

// Rarity colors for UI
export const BADGE_RARITY_COLORS: Record<BadgeRarity, { bg: string; border: string; text: string; glow?: string }> = {
  common: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-600' },
  uncommon: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700' },
  rare: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700' },
  epic: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700', glow: 'shadow-purple-300' },
  legendary: { bg: 'bg-amber-100', border: 'border-amber-400', text: 'text-amber-700', glow: 'shadow-amber-300' },
  secret: { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-700', glow: 'shadow-pink-300' }
};

// Legacy Badge interface for backwards compatibility
export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  xpReward: number;
  condition: 'attendance_streak' | 'assignment_streak' | 'grade_threshold' | 'participation' | 'first_submit' | 'perfect_week';
  threshold?: number;
}

export interface XPEvent {
  id: string;
  studentId: string;
  type: 'assignment_submit' | 'assignment_graded' | 'attendance' | 'participation' | 'badge_earned';
  xp: number;
  description: string;
  timestamp: string;
}

export interface GamificationSettings {
  enabled: boolean;
  showLeaderboard: boolean;
  anonymousLeaderboard: boolean; // When true, students only see their own position, others are anonymized
  xpValues: {
    assignmentSubmit: number;
    assignmentOnTime: number;
    gradeBonus80: number;
    gradeBonus90: number;
    perfectAttendance: number;
    participation: number;
  };
}

/**
 * A school member who has been removed but can still be put back.
 *
 * Removal is always a soft-remove: there is no permanent-delete action in the
 * product. The realistic failure is not a hoarded record — it is someone removed
 * by mistake, or in anger, and an admin finding on Monday that there is nothing
 * to undo. Access is revoked immediately regardless; what survives is the
 * ability to reverse it.
 */
export interface RemovedMember {
  uid: string;
  role: string;
  wasStaff: boolean;
  removedAt: string;
  removedBy: string;
  removedByName?: string;
  /** When the removal stops being reversible. */
  expiresAt: string;
  displayName?: string;
  email?: string;
  /** Classes they owned, orphaned on removal and handed back on restore. */
  ownedClassIds: string[];
  /** Classes they co-taught. Recorded because the class keeps no trace of them
   *  once they leave the array, so a restore could not otherwise find them. */
  coTaughtClassIds: string[];
}
